import React, { createContext, useCallback, useContext, useState } from "react";

/**
 * UI-only localization layer (English ↔ Bahasa Indonesia).
 * - English source strings are used as keys: t("New Order").
 * - Untranslated strings fall back to the English key, so nothing ever breaks.
 * - Backend enums / DB values / identifiers are NEVER touched.
 * Preference is persisted per device in localStorage ("cf_lang"), default "id".
 */

const ID = {
  // ---- Attendance geofence (Branch Editor · HR Production milestone) ----
  "Attendance Location (GPS)": "Lokasi Absensi (GPS)",
  "Use Current Location": "Gunakan Lokasi Saat Ini",
  "Radius (m)": "Radius (m)",
  "Location captured": "Lokasi berhasil diambil",
  "GPS not supported on this device": "GPS tidak didukung perangkat ini",
  "Unable to get location. Enable GPS and allow permission.": "Tidak dapat mengambil lokasi. Aktifkan GPS dan izinkan akses.",
  // ---- Workspace navigation (Production Baseline v1.0) ----
  "Order Status": "Status Pesanan",
  "POS": "POS / Kasir",
  "Sales Upload": "Unggah Penjualan",
  "Branch Purchase Review": "Tinjau Pembelian Cabang",
  "Delivery Verification": "Verifikasi Pengiriman",
  "Transfer Stock": "Transfer Stok",
  "Master Preparation": "Master Persiapan",
  "Verifikasi": "Verifikasi",
  "Rantai Pasok": "Rantai Pasok",
  "Gudang Pusat": "Gudang Pusat",
  "Produksi Pusat": "Produksi Pusat",
  "Operasi Cabang": "Operasi Cabang",
  "Analitik": "Analitik",
  "Administrasi": "Administrasi",
  // ---- Photo evidence + Loss & Incident (Production Baseline v1.0) ----
  "Camera": "Kamera",
  "Gallery": "Galeri",
  "Photo Evidence": "Bukti Foto",
  "required": "wajib",
  "Attach at least 1 photo of the preparation result": "Lampirkan minimal 1 foto hasil persiapan",
  "Attach at least 1 photo as evidence": "Lampirkan minimal 1 foto sebagai bukti",
  "Photo evidence is required (min 1)": "Foto bukti wajib (minimal 1)",
  "Estimated Loss Value": "Estimasi Nilai Kerugian",
  "Open": "Open",
  "Under Review": "Ditinjau",
  "Return": "Kembalikan",
  "Review": "Tinjau",

  // --- UX Refinements: operational foundation completion ---
  "In Process": "Dalam Proses",
  "Tomorrow's Branch Order": "Pesanan Cabang Besok",
  "Tomorrow's Order Recommendation": "Rekomendasi Pesanan Besok",
  "Generate Recommended Order": "Buat Pesanan Rekomendasi",
  "Based on ideal stock, last 7 days usage, current stock and goods in transit.": "Berdasarkan stok ideal, pemakaian 7 hari terakhir, stok saat ini, dan barang dalam perjalanan.",
  "recommended items added": "item rekomendasi ditambahkan",
  "Stock is healthy — no recommendation needed": "Stok sehat — tidak perlu rekomendasi",
  "Continue Stock Take": "Lanjutkan Stok Opname",
  "Start Stock Take": "Mulai Stok Opname",
  "View Result": "Lihat Hasil",
  "Start a New Stock Take": "Mulai Stok Opname Baru",
  "Document ID": "ID Dokumen",
  "You have a stock take in progress. Continue where you left off.": "Ada stok opname yang sedang berjalan. Lanjutkan dari yang terakhir.",
  "Reconcile your branch stock against the system.": "Cocokkan stok cabang Anda dengan sistem.",
  "Your latest stock take is complete. View the result or start a new one.": "Stok opname terakhir Anda selesai. Lihat hasil atau mulai yang baru.",
  "Receiving → Preparation → Waste → Stock Take → Tomorrow's Branch Order → Close Kitchen": "Penerimaan → Persiapan → Barang Rusak → Stok Opname → Pesanan Cabang Besok → Tutup Dapur",
  "Fresh Market Shopping List": "Daftar Belanja Fresh Market",
  "Total quantity per product across all branches — the purchasing team's market shopping list.": "Total kebutuhan per produk dari semua cabang — daftar belanja tim pembelian di pasar.",
  "Total to Buy": "Total Beli",
  "Branch Order Summary": "Ringkasan Pesanan Cabang",
  "Approve Branch Orders": "Setujui Pesanan Cabang",
  // --- Central Production guided workflow ---
  "Central Production": "Central Production",
  "Auto-recommended production, guided workflow, QC & packing — powered by the existing engines.": "Rekomendasi produksi otomatis, alur terpandu, QC & packing — memakai engine yang sudah ada.",
  "Owner Summary": "Ringkasan Owner",
  "Est. Finish": "Estimasi Selesai",
  "Today's Target": "Target Hari Ini",
  "Remaining": "Sisa",
  "In Production": "Sedang Produksi",
  "QC Waiting": "Menunggu QC",
  "Ready for Shipment": "Siap Kirim",
  "Completion": "Penyelesaian",
  "batches": "batch",
  "Production Recommendation": "Rekomendasi Produksi",
  "Rule-based: Branch Demand + Minimum Stock − Current Stock − Open Production. Edit qty before starting.": "Berbasis aturan: Permintaan Cabang + Stok Minimum − Stok Saat Ini − Produksi Berjalan. Ubah jumlah sebelum mulai.",
  "Central stock is healthy — no production needed right now.": "Stok pusat sehat — belum perlu produksi saat ini.",
  "Branch Demand": "Permintaan Cabang",
  "Min": "Min",
  "Stock": "Stok",
  "Open": "Berjalan",
  "Produce": "Produksi",
  "Add to Queue": "Tambah ke Antrean",
  "added to queue": "ditambahkan ke antrean",
  "Enter a quantity": "Masukkan jumlah",
  "Today's Production Queue": "Antrean Produksi Hari Ini",
  "No active production. Add items from the recommendation above.": "Belum ada produksi aktif. Tambahkan dari rekomendasi di atas.",
  "Plan": "Rencana",
  "Validate & Start": "Validasi & Mulai",
  "Record Result": "Catat Hasil",
  "Quality Control": "Quality Control (QC)",
  "Packing": "Packing",
  "Phase": "Fase",
  "Next Phase": "Fase Berikutnya",
  "QC failed — re-check and resubmit. Inventory pending.": "QC gagal — periksa ulang & kirim lagi. Stok tertahan.",
  "Completed Today": "Selesai Hari Ini",
  "No completed production yet today.": "Belum ada produksi yang selesai hari ini.",
  "Batch": "Batch",
  "Exp": "Exp",
  "Yield": "Yield",
  "Print Label": "Cetak Label",
  "Raw Material Validation": "Validasi Bahan Baku",
  "Start Production": "Mulai Produksi",
  "Insufficient materials — production cannot start.": "Bahan baku tidak cukup — produksi tidak bisa dimulai.",
  "Production started": "Produksi dimulai",
  "Actual Production Result": "Hasil Produksi Aktual",
  "Submit to QC": "Kirim ke QC",
  "Actual Output": "Output Aktual",
  "optional": "opsional",
  "Yield, costing & inventory are auto-calculated by the engine after QC & packing.": "Yield, costing & inventory dihitung otomatis oleh engine setelah QC & packing.",
  "Result recorded · sent to QC": "Hasil dicatat · dikirim ke QC",
  "Tick each passing check. If any fails, inventory stays pending.": "Centang setiap pemeriksaan yang lolos. Jika ada yang gagal, stok tertahan.",
  "Submit QC": "Kirim QC",
  "QC passed · ready to pack": "QC lulus · siap dikemas",
  "QC failed · inventory stays pending": "QC gagal · stok tetap tertahan",
  "Complete Packing": "Selesaikan Packing",
  "Package Label": "Label Kemasan",
  "Package Count": "Jumlah Kemasan",
  "Expiry Date": "Tanggal Kedaluwarsa",
  "auto if shelf-life set": "otomatis jika shelf-life diisi",
  "On completion the engine consumes materials, creates finished goods, and posts costing & ledger.": "Saat selesai, engine memotong bahan baku, membuat barang jadi, dan mencatat costing & ledger.",
  "Packed": "Dikemas",
  "Label": "Label",
  "Appearance": "Tampilan",
  "Aroma": "Aroma",
  "Taste": "Rasa",
  "Weight": "Berat",
  "Shelf Life (days)": "Masa Simpan (hari)",
  "Branch Picking List": "Picking List Cabang",
  "Picking List": "Picking List",
  "Production Queue": "Antrean Produksi",
  "Recommendation": "Rekomendasi",
  "Material Validation": "Validasi Bahan",
  "Transfer to Branch": "Transfer ke Cabang",
  // ---- Central Supply Chain (Milestone 4) ----
  "Central Supply Chain": "Rantai Pasok Pusat",
  "Supply Chain Control Center": "Pusat Kendali Rantai Pasok",
  "Supply Chain Traceability": "Telusur Rantai Pasok",
  // ---- Loss & Incident Management + Morning Operations (Enhancements) ----
  "Morning Ops": "Operasi Pagi",
  "Loss & Incident": "Kerugian & Insiden",
  "Loss & Incident Management": "Manajemen Kerugian & Insiden",
  "Complete visibility into every operational loss — not just damaged stock": "Visibilitas penuh atas setiap kerugian operasional — bukan hanya stok rusak",
  "Total Loss Value": "Total Nilai Kerugian",
  "Food Cost Impact": "Dampak Biaya Bahan",
  "Total Incidents": "Total Insiden",
  "Awaiting Approval": "Menunggu Persetujuan",
  "Report an Incident": "Laporkan Insiden",
  "records": "catatan",
  "Recent Incidents": "Insiden Terbaru",
  "No incidents recorded": "Belum ada insiden tercatat",
  "Report": "Laporkan",
  "Select a menu item": "Pilih item menu",
  "Incident recorded": "Insiden tercatat",
  "Select reason": "Pilih alasan",
  "Select menu": "Pilih menu",
  "Platform": "Platform",
  "Select platform": "Pilih platform",
  "Refund Amount": "Jumlah Refund",
  "Remake Amount": "Jumlah Pembuatan Ulang",
  "Delivery / Order Ref": "Ref Pengiriman / Pesanan",
  "Morning Operations": "Operasi Pagi",
  "Night Operations": "Operasi Malam",
  "Morning Ops is for branch accounts. Select a branch from the dashboard.": "Operasi Pagi hanya untuk akun cabang. Pilih cabang dari dasbor.",
  "incoming": "masuk",
  "Restaurant ready for business!": "Restoran siap beroperasi!",
  "All deliveries received and stored.": "Semua pengiriman telah diterima dan disimpan.",
  "Incoming Deliveries": "Pengiriman Masuk",
  "Receive & Inspect": "Terima & Periksa",
  "Completed": "Selesai",
  "Receive Delivery": "Terima Pengiriman",
  "Verify Delivery Note": "Verifikasi Surat Jalan",
  "Check Delivered Items": "Periksa Barang Terkirim",
  "Report Shortage / Damage": "Laporkan Kekurangan / Kerusakan",
  "Store Inventory": "Simpan ke Inventaris",
  "Ready for Business": "Siap Beroperasi",
  "Receive & Inspect Delivery": "Terima & Periksa Pengiriman",
  "Back to": "Kembali ke",
  "Finish this task, then continue your guided flow.": "Selesaikan tugas ini, lalu lanjutkan alur terpandu Anda.",
  "Prepare Order": "Siapkan Pesanan",
  // ---- Central Operations (Night + Morning) ----
  "Central Night": "Pusat — Malam",
  "Central Morning": "Pusat — Pagi",
  "Central Operations Night": "Operasi Pusat — Malam",
  "Central Operations Morning": "Operasi Pusat — Pagi",
  // ---- Milestone 3: Branch Kitchen Operations ----
  "Branch Kitchen Operations": "Alur Operasional Dapur",
  "Daily kitchen command center": "Alur kerja harian dapur",
  "Kitchen Readiness": "Kesiapan Dapur",
  "READY": "SIAP",
  "NOT READY": "BELUM SIAP",
  "Kitchen is ready to operate": "Dapur siap beroperasi",
  "Complete all checks to open the kitchen": "Selesaikan semua cek untuk membuka dapur",
  "Today's delivery received": "Pengiriman hari ini diterima",
  "Mandatory preparation completed": "Persiapan wajib selesai",
  "No preparation in Draft / In Progress": "Tidak ada persiapan Draf / Berjalan",
  "No critical Ready-to-Cook out of stock": "Tidak ada Bahan Siap Masak kritis yang habis",
  "Today's Deliveries": "Pengiriman Hari Ini",
  "Raw Inventory": "Bahan Baku",
  "RTC Inventory": "Stok Bahan Siap Masak",
  "Today's Waste": "Limbah Hari Ini",
  "Yield Performance": "Performa Rendemen",
  "Daily Workflow": "Alur Harian",
  "Kitchen Dashboard": "Dashboard Dapur",
  "Daily Preparation": "Persiapan Harian",
  "Ready-to-Cook Inventory": "Stok Bahan Siap Masak",
  "Kitchen Closing": "Tutup Dapur",
  "Inventory Split": "Pemisahan Stok",
  "One standard workflow, every day": "Satu alur standar, setiap hari",
  "Receiving → Preparation → Ready-to-Cook → Service → Stock Take → Closing": "Terima → Persiapan → Bahan Siap Masak → Operasi → Stok Opname → Tutup",
  "Done": "Selesai",
  "Active": "Aktif",
  "Locked": "Terkunci",
  "low": "rendah",
  "critical": "kritis",
  "healthy": "sehat",
  // ---- Central Operations: 3 shifts (Night/Morning/Afternoon) ----
  "Central Operations": "Operasi Pusat",
  "Night": "Malam",
  "Morning": "Pagi",
  "Afternoon": "Siang",
  "Central Operations Afternoon": "Operasi Pusat — Siang",
  "Planning — consolidate branch orders & fresh market": "Perencanaan — ringkasan pesanan cabang & fresh market",
  "Logistics — receive, verify, pack & dispatch": "Logistik — terima, verifikasi, packing & kirim",
  "Production — Central Kitchen Production Hub": "Produksi — Pusat Kendali Produksi Dapur Pusat",
  "Central Kitchen Production Hub": "Pusat Kendali Produksi Dapur Pusat",
  "Produces sauces, seasonings & semi-finished goods, then transfers to": "Memproduksi saus, bumbu & barang setengah jadi, lalu transfer ke",
  "Branch Kitchen": "Dapur Cabang",
  // Afternoon hub — sections & KPIs
  "Production Lines": "Lini Produksi",
  "Sauce Production": "Produksi Saus",
  "Central Seasoning": "Bumbu Pusat",
  "Semi-Finished Products": "Barang Setengah Jadi",
  "Finished Products": "Produk Jadi",
  "Batch Production": "Produksi Batch",
  "Transfer to Branches": "Transfer ke Cabang",
  "Produced Today": "Diproduksi Hari Ini",
  "Open Drafts": "Draf Terbuka",
  "Avg Yield": "Rata-rata Rendemen",
  "In Transit": "Dalam Pengiriman",
  "Ready to Transfer": "Siap Ditransfer",
  "awaiting confirmation": "menunggu konfirmasi",
  "to branches": "ke cabang",
  "products in stock": "produk ada stok",
  "products": "produk",
  "recipes": "resep",
  "batches today": "batch hari ini",
  "Open Production": "Buka Produksi",
  "New Batch": "Batch Baru",
  "Recipes": "Resep",
  "Send Transfer": "Kirim Transfer",
  "in transit": "dikirim",
  "received": "diterima",
  "done": "selesai",
  "drafts": "draf",
  "output": "output",
  "Today's Batches": "Batch Hari Ini",
  "Active Transfers": "Transfer Aktif",
  "No production activity today": "Belum ada aktivitas produksi hari ini",
  "No active transfers": "Tidak ada transfer aktif",
  "yield": "rendemen",
  "Confirmed": "Selesai",
  "Cancelled": "Dibatalkan",
  "IN_TRANSIT": "Dikirim",
  "RECEIVED": "Diterima",
  "PARTIAL": "Sebagian",
  "Prepare tomorrow's operations": "Siapkan operasi besok",
  "Receive goods, verify picking & dispatch deliveries": "Terima barang, verifikasi picking & berangkatkan pengiriman",
  "Today's Progress": "Progres Hari Ini",
  "orders updated": "pesanan diperbarui",
  "Receive Branch Orders": "Terima Pesanan Cabang",
  "Review Auto Consolidation": "Tinjau Konsolidasi Otomatis",
  "Approve Consolidation": "Setujui Konsolidasi",
  "Fresh Market Purchasing": "Belanja Fresh Market",
  "No perishables today": "Tidak ada barang segar hari ini",
  "Mark as Ordered": "Tandai Sudah Dipesan",
  "Marked as Ordered": "Sudah Dipesan",
  "Print / Export PDF": "Cetak / Ekspor PDF",
  "Username": "Username",
  "Email Settings": "Pengaturan Email",
  "Email Notifications": "Notifikasi Email",
  "Resend provider status, sender & delivery logs": "Status provider Resend, pengirim & log pengiriman",
  "Email Provider Status": "Status Provider Email",
  "Not configured": "Belum dikonfigurasi",
  "Set the RESEND_API_KEY secret in your deployment to enable sending. Sender & service are ready.": "Atur secret RESEND_API_KEY di deployment Anda untuk mengaktifkan pengiriman. Pengirim & layanan sudah siap.",
  "Sender Email": "Email Pengirim",
  "Sender Name": "Nama Pengirim",
  "Last Email Sent": "Email Terakhir Dikirim",
  "Never": "Belum pernah",
  "Send Test Email": "Kirim Email Uji",
  "Leave blank to send to your own profile email.": "Kosongkan untuk mengirim ke email profil Anda sendiri.",
  "Disabled until RESEND_API_KEY is configured.": "Nonaktif sampai RESEND_API_KEY dikonfigurasi.",
  "Recent Email Logs": "Log Email Terbaru",
  "No email activity yet.": "Belum ada aktivitas email.",
  "Failed to load email status": "Gagal memuat status email",
  "Test email sent to": "Email uji terkirim ke",
  "Could not send": "Gagal mengirim",
  "Recipient": "Penerima",
  "Detail": "Detail",
  "sent": "terkirim",
  "skipped": "dilewati",
  "failed": "gagal",
  "Print": "Cetak",
  "Export PDF": "Ekspor PDF",
  "downloaded": "diunduh",
  "Coming Soon": "Segera Hadir",
  "Warehouse Preparation": "Persiapan Gudang",
  "Check each item while preparing tomorrow's delivery — recorded for the audit trail.": "Centang setiap item saat menyiapkan pengiriman besok — tercatat untuk jejak audit.",
  "Mark Picking Prepared": "Tandai Picking Siap",
  "Picking Prepared": "Picking Siap",
  "Tomorrow is Ready": "Besok Siap Beroperasi",
  "Central Night Complete": "Operasi Malam Pusat Selesai",
  "Hands over to": "Diserahkan ke",
  "Receive Fresh Market Goods": "Terima Barang Fresh Market",
  "Verify Warehouse Picking": "Verifikasi Picking Gudang",
  "Nothing to verify yet": "Belum ada yang perlu diverifikasi",
  "Packing & Delivery Notes": "Packing & Surat Jalan",
  "All packed": "Semua sudah dikemas",
  "Verify Done → Packing": "Verifikasi Selesai → Packing",
  "Mark Packed & Ready": "Tandai Dikemas & Siap",
  "Dispatch Deliveries": "Berangkatkan Pengiriman",
  "Nothing to dispatch": "Tidak ada yang perlu diberangkatkan",
  "Assign Driver": "Tugaskan Driver",
  "Assigned to": "Ditugaskan ke",
  "Shipment dispatched — awaiting branch receive": "Pengiriman berangkat — menunggu penerimaan cabang",
  "READY": "Siap",
  "LOADED": "Dimuat",
  "IN_TRANSIT": "Dalam Perjalanan",
  "Wait for Branch Confirmation": "Tunggu Konfirmasi Cabang",
  "open variances need attention": "selisih terbuka perlu ditindaklanjuti",
  "No deliveries out yet": "Belum ada pengiriman keluar",
  "Open in Control Tower": "Buka di Menara Kontrol",
  "Warehouse Picking": "Picking Gudang",
  "Packing": "Packing",
  "Branch Confirmation": "Konfirmasi Cabang",
  "Fresh Market category set": "Kategori Fresh Market diatur",
  "Fresh Market category cleared": "Kategori Fresh Market dihapus",
  "Set as Fresh Market category": "Jadikan kategori Fresh Market",
  "Tap the leaf to mark which category is the Fresh Market category. Its products route to Fresh Market purchasing; all others go to Central Warehouse.": "Ketuk ikon daun untuk menandai kategori Fresh Market. Produk di kategori ini masuk ke pembelian Fresh Market; lainnya ke Gudang Central.",
  "Current Owner": "Pemilik Saat Ini",
  "Driver": "Driver",
  "Failed to open document": "Gagal membuka dokumen",
  "Submitted to Central": "Dikirim ke Pusat",
  "Assigned": "Ditugaskan",
  "Branch Receiving": "Penerimaan Cabang",
  "Picking": "Picking",
  // ---- Stabilization pass: inbox, fresh verification, doc printing ----
  "Generating document…": "Membuat dokumen…",
  "Document": "Dokumen",
  "ready": "siap",
  "opened": "dibuka",
  "Print Delivery Note": "Cetak Surat Jalan",
  "Incoming Orders": "Kotak Masuk Pesanan",
  "Incoming Orders Inbox": "Kotak Masuk Pesanan",
  "Live inbox of every branch order — who submitted, who is late, who is still editing.": "Kotak masuk langsung setiap pesanan cabang — siapa yang sudah mengirim, terlambat, atau masih mengedit.",
  "No orders received yet": "Belum ada pesanan masuk",
  "Branches not yet submitted": "Cabang belum mengirim",
  "All branches have submitted": "Semua cabang sudah mengirim",
  "Editing": "Sedang Diedit",
  "Not submitted": "Belum Dikirim",
  "View details": "Lihat detail",
  "Tap any order to open full details": "Ketuk pesanan untuk membuka detail lengkap",
  "Fresh Market Verification": "Verifikasi Fresh Market",
  "Fresh Market Delivery": "Pengiriman Fresh Market",
  "Verify each branch's fresh goods on arrival. A shortage or damage automatically creates a variance before packing.": "Verifikasi barang segar tiap cabang saat tiba. Kekurangan atau kerusakan otomatis membuat selisih sebelum packing.",
  "Verify": "Verifikasi",
  "Verified": "Terverifikasi",
  "Re-verify": "Verifikasi Ulang",
  "Ordered Qty": "Jml Dipesan",
  "Received Qty": "Jml Diterima",
  "Damaged Qty": "Jml Rusak",
  "Variance created": "Selisih dibuat",
  "Complete fresh & picking verification before packing": "Selesaikan verifikasi Fresh Market & Picking sebelum packing",
  "Fresh Market Purchase List": "Daftar Belanja Fresh Market",
  "Grouped by branch — the supplier separates vegetables per branch before delivery.": "Dikelompokkan per cabang — pemasok memisahkan sayur per cabang sebelum pengiriman.",
  "Grand Total": "Total Keseluruhan",
  "Total (all branches)": "Total (semua cabang)",
  "No fresh market items today": "Tidak ada barang Fresh Market hari ini",
  "All fresh goods verified": "Semua barang segar terverifikasi",
  // ---- UAT round 2: Orders redesign + Morning Ops pending ----
  "Earlier": "Lebih Lama",
  "Total Today": "Total Hari Ini",
  "Processing": "Diproses",
  "Active Orders": "Pesanan Aktif",
  "Completed Orders": "Pesanan Selesai",
  "No orders match your search": "Tidak ada pesanan yang cocok dengan pencarian",
  "No active orders — all caught up!": "Tidak ada pesanan aktif — semua beres!",
  "Search by branch, reference or product…": "Cari berdasarkan cabang, nomor, atau produk…",
  "Outstanding Work": "Pekerjaan Tertunda",
  "items waiting": "item menunggu",
  "items remaining": "item tersisa",
  "orders waiting": "pesanan menunggu",
  "drivers unassigned": "driver belum ditugaskan",
  "branches waiting": "cabang menunggu",
  "All clear": "Beres",
  // ---- Meja Prepare (Production Preparation Center) ----
  "Convert raw ingredients into ready-to-cook — with yield & shrinkage": "Ubah bahan baku menjadi Bahan Siap Masak — dengan rendemen & penyusutan",
  "Prepare": "Persiapan",
  "Search ingredient…": "Cari bahan…",
  "No prepared products configured": "Belum ada Bahan Siap Masak yang dikonfigurasi",
  "Ready": "Siap",
  "To prepare": "Perlu Disiapkan",
  "from": "dari",
  "No source set": "Bahan baku belum diatur",
  "Source out of stock": "Stok bahan baku habis",
  "Today's Preparation": "Persiapan Hari Ini",
  "Nothing prepared yet today": "Belum ada persiapan hari ini",
  "Review Required": "Perlu Ditinjau",
  "No preparation records yet": "Belum ada catatan persiapan",
  "Review": "Tinjau",
  "Raw Weight": "Berat Bahan Baku",
  "Prepared Weight": "Berat Hasil",
  "Shrinkage": "Penyusutan",
  "exp": "perkiraan",
  "Shrinkage Reason": "Alasan Penyusutan",
  "Before Photo": "Foto Sebelum",
  "After Photo": "Foto Sesudah",
  "Add photo": "Tambah foto",
  "Uploading…": "Mengunggah…",
  "Confirm Preparation": "Konfirmasi Persiapan",
  "Yield outside expected range": "Rendemen di luar rentang yang diharapkan",
  "preparation recorded": "persiapan tercatat",
  "Cancelled & reversed": "Dibatalkan & dikembalikan",
  "Cancel & Reverse": "Batalkan & Kembalikan",
  "Failed to upload photo": "Gagal mengunggah foto",
  "CONFIRMED": "Dikonfirmasi",
  "APPROVED": "Disetujui",
  "CANCELLED": "Dibatalkan",
  "Kitchen / Head Chef": "Dapur / Kepala Koki",
  "reason_bone": "Tulang",
  "reason_fat": "Lemak",
  "reason_skin": "Kulit",
  "reason_shell": "Cangkang",
  "reason_stem": "Batang",
  "reason_trimming": "Potongan",
  "reason_damaged_leaves": "Daun Rusak",
  "reason_cleaning": "Pembersihan",
  "reason_other": "Lainnya",

  // ---- Meja Prepare — Raw-centric multi-output + Configuration ----
  "Preparation Configuration": "Konfigurasi Persiapan",
  "Search raw material or output…": "Cari bahan baku atau output…",
  "Total Output": "Total Hasil",
  "Overall Yield": "Rendemen Keseluruhan",
  "Add Configuration": "Tambah Konfigurasi",
  "outputs": "output",
  "Possible Outputs": "Kemungkinan Output",
  "Raw materials": "Bahan baku",
  "No raw materials configured": "Belum ada bahan baku yang dikonfigurasi",
  "Prepared Products": "Produk Olahan",
  "possible outputs": "kemungkinan output",
  "Outputs Produced": "Output yang Dihasilkan",
  "Select a raw material": "Pilih bahan baku",
  "Select at least one output": "Pilih minimal satu output",
  "Save Configuration": "Simpan Konfigurasi",
  "Edit Configuration": "Ubah Konfigurasi",
  "Choose the raw material from available stock": "Pilih bahan baku dari stok tersedia",
  "2. Select Outputs": "2. Pilih Output",
  "Tick the outputs you produce today": "Centang output yang dibuat hari ini",
  "3. Enter Raw Weight": "3. Isi Berat Bahan Baku",
  "Input the total weight taken": "Masukkan total berat yang diambil",
  "4. Enter Each Result": "4. Isi Tiap Hasil",
  "Weigh & input each output manually": "Timbang & isi tiap output secara manual",
  "Select outputs produced today": "Pilih output yang dibuat hari ini",
  "Enter actual weight for each output": "Isi berat aktual tiap output",
  "Enter actual weight for each output on the next step.": "Isi berat aktual tiap output pada langkah berikutnya.",
  "Total result cannot exceed raw weight": "Total hasil tidak boleh melebihi berat bahan baku",
  "Map each raw material to its possible prepared outputs": "Petakan tiap bahan baku ke kemungkinan output olahannya",
  "Configured Raw Materials": "Bahan Baku Terkonfigurasi",
  "No outputs configured": "Belum ada output dikonfigurasi",
  "No configuration yet": "Belum ada konfigurasi",
  "Delete this configuration?": "Hapus konfigurasi ini?",
  "Configuration deleted": "Konfigurasi dihapus",
  "Configuration saved": "Konfigurasi disimpan",
  "outputs selected": "output dipilih",
  "Search output…": "Cari output…",
  "No prepared products available": "Tidak ada produk olahan tersedia",
  "in another config": "di konfigurasi lain",
  "Yield % is the expected portion of raw that becomes this output (optional, for review flags).": "Rendemen % adalah perkiraan bagian bahan baku yang menjadi output ini (opsional, untuk penanda tinjauan).",
  // ---- Daily Preparation Session ----
  "Session History": "Riwayat Sesi",
  "One daily session to prep the whole kitchen — Raw → Semi-Finished": "Satu sesi harian untuk menyiapkan seluruh dapur — Bahan Baku → Barang Setengah Jadi",
  "One daily session to prep the whole kitchen — Raw → Ready-to-Cook": "Satu sesi harian untuk menyiapkan seluruh dapur — Bahan Baku → Bahan Siap Masak",
  "Preparation Session": "Sesi Persiapan",
  "OPEN": "BUKA",
  "CLOSED": "DITUTUP",
  "Add Raw Material": "Tambah Bahan Baku",
  "Close Session": "Tutup Sesi",
  "Reopen Session": "Buka Kembali Sesi",
  "Total Input": "Total Input",
  "Total Waste": "Total Waste",
  "Ingredients Done": "Bahan Selesai",
  "Today's Ingredients": "Bahan Hari Ini",
  "No ingredients added yet": "Belum ada bahan ditambahkan",
  "Fill & Finish": "Isi & Selesaikan",
  "Input": "Input",
  "Inventory Result": "Hasil Inventory",
  "Raw Inventory (Remaining)": "Stok Bahan Baku (Sisa)",
  "Head Chef Dashboard": "Dashboard Head Chef",
  "Full report": "Laporan lengkap",
  "Top Output": "Output Teratas",
  "Add to Session": "Tambah ke Sesi",
  "Save & Finish": "Simpan & Selesaikan",
  "Unit Conversion (optional)": "Konversi Satuan (opsional)",
  "Base unit (for yield)": "Satuan dasar (untuk yield)",
  "Use when purchase/stock unit differs from the yield unit (e.g. 1 ekor = 1.2 kg). Leave as 1 if same.": "Gunakan bila satuan beli/stok berbeda dari satuan yield (mis. 1 ekor = 1,2 kg). Biarkan 1 bila sama.",
  "Expected yield %": "Rendemen ekspektasi %",
  "No preparation session today": "Belum ada sesi persiapan hari ini",
  "After receiving stock from Central, open today's session to prepare the whole kitchen — one ingredient at a time.": "Setelah menerima stok dari Pusat, buka sesi hari ini untuk menyiapkan seluruh dapur — satu bahan demi satu bahan.",
  "Open Preparation Session": "Buka Sesi Persiapan",
  "Log in as Head Chef to open a session.": "Masuk sebagai Head Chef untuk membuka sesi.",
  "Preparation session opened": "Sesi persiapan dibuka",
  "Session closed — kitchen ready": "Sesi ditutup — dapur siap",
  "Close today's session? Inventory already moved per finalized item.": "Tutup sesi hari ini? Inventory sudah berpindah per bahan yang diselesaikan.",
  "Remove this ingredient from the session?": "Hapus bahan ini dari sesi?",
  "Undo this finalized ingredient? Stock will be reversed.": "Batalkan bahan yang sudah selesai ini? Stok akan dikembalikan.",
  "Reversed": "Dibatalkan",
  "added to session": "ditambahkan ke sesi",
  "Search raw material…": "Cari bahan baku…",
  "No configured raw materials": "Belum ada bahan baku terkonfigurasi",
  "Input Quantity": "Jumlah Input",
  "finalized & moved to Ready-to-Cook": "selesai & berpindah ke Bahan Siap Masak",
  "Select outputs produced today & enter actual weight": "Pilih output yang dibuat hari ini & isi berat aktual",
  "Total result cannot exceed input": "Total hasil tidak boleh melebihi input",
  "Waste Reason": "Alasan Waste",
  "No sessions yet": "Belum ada sesi",
  "Today's Waste": "Waste Hari Ini",
  "No raw materials": "Tidak ada bahan baku",
  "No ready-to-cook stock yet": "Belum ada stok Bahan Siap Masak",


  // ---- Meja Prepare — Dashboard / Analytics (Phase 2) ----
  "Prepare Dashboard": "Dashboard Persiapan",
  "Yield, shrinkage & performance analytics for Meja Prepare": "Analitik rendemen, penyusutan & performa Meja Prepare",
  "7 days": "7 Hari",
  "30 days": "30 Hari",
  "90 days": "90 Hari",
  "This month": "Bulan Ini",
  "Overview": "Ringkasan",
  "Yield Analysis": "Analisa Rendemen",
  "Supplier Comparison": "Perbandingan Pemasok",
  "Employee Comparison": "Perbandingan Karyawan",
  "Trend": "Tren",
  "Total Prepared": "Total Disiapkan",
  "Avg Yield": "Rata-rata Rendemen",
  "Total Shrinkage": "Total Penyusutan",
  "Total Raw": "Total Bahan Baku",
  "Needs Review": "Perlu Ditinjau",
  "Prepared Today": "Disiapkan Hari Ini",
  "To Prepare": "Perlu Disiapkan",
  "Total Processed": "Total Diproses",
  "In progress now": "Sedang berjalan",
  "Completed today": "Selesai hari ini",
  "yield out of range": "rendemen di luar batas",
  "output": "hasil",
  "overall": "keseluruhan",
  "avg": "rata-rata",
  "today": "hari ini",
  "Top by Volume": "Terbanyak Berdasarkan Volume",
  "Most prepared in this period": "Paling banyak disiapkan pada periode ini",
  "No data for this period": "Tidak ada data untuk periode ini",
  "Workload & Status": "Beban Kerja & Status",
  "Preparation pipeline this period": "Alur persiapan pada periode ini",
  "Records": "Catatan",
  "Prepared": "Disiapkan",
  "prepared": "disiapkan",
  "Expected": "Perkiraan",
  "Variance": "Selisih",
  "Yield by Supplier": "Rendemen per Pemasok",
  "Higher yield = better raw material quality": "Rendemen lebih tinggi = kualitas bahan baku lebih baik",
  "Yield by Employee": "Rendemen per Karyawan",
  "Consistency of preparation per staff": "Konsistensi persiapan per staf",
  "Yield Trend": "Tren Rendemen",
  "Average yield % over time": "Rata-rata rendemen % dari waktu ke waktu",
  "Prepared Quantity Trend": "Tren Jumlah Hasil",
  "Prepared output per day": "Hasil persiapan per hari",

  // ---- Meja Prepare — Two-step workflow (Start -> Finish) + Master Data config ----
  "Start Preparation": "Mulai Persiapan",
  "Finish Preparation": "Selesai Persiapan",
  "PREPARING": "Sedang Disiapkan",
  "COMPLETED": "Selesai",
  "Completed": "Selesai",
  "Currently Preparing": "Sedang Disiapkan",
  "Nothing being prepared right now": "Tidak ada yang sedang disiapkan",
  "Select Batch": "Pilih Batch",
  "Latest batch (auto)": "Batch terbaru (otomatis)",
  "No batch": "Tanpa Batch",
  "Batch": "Batch",
  "preparation started": "persiapan dimulai",
  "preparation completed": "persiapan selesai",
  "Raw material moves to Work-In-Progress now. Yield is calculated when you finish.": "Bahan baku berpindah ke status Sedang Diproses (WIP) sekarang. Rendemen dihitung saat Anda menyelesaikan.",
  "Final Weight": "Berat Akhir",
  "Save Changes": "Simpan Perubahan",
  "Changes saved": "Perubahan tersimpan",
  "Cancel & Restore": "Batalkan & Kembalikan",
  "Cancelled & raw restored": "Dibatalkan & bahan baku dikembalikan",
  "Cancelled": "Dibatalkan",
  "Executive Mode (View Only)": "Mode Eksekutif (Hanya Lihat)",
  "Log in as Head Chef to perform preparations.": "Silakan login sebagai Head Chef untuk melakukan persiapan.",
  "Complete Preparation": "Selesaikan Persiapan",
  "Complete": "Selesai",
  "Back": "Kembali",
  "Done Today": "Selesai Hari Ini",
  "No completions yet today": "Belum ada yang selesai hari ini",
  "Currently being prepared. Edit the details below, then complete when finished.": "Sedang disiapkan. Ubah detail di bawah ini, lalu selesaikan setelah persiapan rampung.",
  "Actual Yield": "Rendemen Aktual",
  "Ready-to-Cook Ingredient": "Bahan Siap Masak",
  "Preparation Standard": "Standar Persiapan",
  "Master Data sets the expected standard only — actual yield is decided at Meja Prepare.": "Master Data hanya menetapkan standar yang diharapkan — hasil aktual selalu ditentukan di Meja Prepare.",
  "Source Raw Material": "Bahan Baku Sumber",
  "Select source raw material": "Pilih bahan baku sumber",
  "Expected Yield (%)": "Perkiraan Rendemen (%)",
  "Yield Tolerance (%)": "Toleransi Rendemen (%)",
  "This Ready-to-Cook ingredient appears in Meja Prepare automatically once a source raw material is set.": "Bahan Siap Masak ini otomatis muncul di Meja Prepare setelah bahan baku sumber diatur.",
  "Raw Stock": "Stok Bahan Baku",
  "Est. Ready-to-Cook": "Estimasi Bahan Siap Masak",
  "Standard yield": "Rendemen standar",
  "In stock": "Tersedia",
  "Today's kitchen workboard — turn raw materials into ready-to-cook": "Papan kerja dapur hari ini — ubah bahan baku menjadi Bahan Siap Masak",
  "Tap a product to start preparing": "Ketuk produk untuk mulai menyiapkan",
  "Total Processed Today": "Total Diproses Hari Ini",
  "Working": "Dikerjakan",
  "Save & Complete": "Simpan & Selesai",

  // ---- Meja Prepare — Central Kitchen Production Dashboard (Desktop redesign) ----
  "Meja Prepare": "Meja Prepare",
  "Convert raw materials into ready-to-cook — with measured yield & shrinkage": "Ubah bahan baku menjadi bahan siap masak — dengan rendemen & penyusutan terukur",
  "Active Warehouse": "Gudang Aktif",
  "Meja Prepare operates on the Central Warehouse": "Meja Prepare beroperasi pada Gudang Pusat",
  "Active batches": "Batch aktif",
  "Total output": "Total output",
  "Total Produced": "Total Diproduksi",
  "Avg Yield Result": "Rata-rata Hasil",
  "Today's yield": "Rendemen hari ini",
  "View list": "Lihat daftar",
  "View detail": "Lihat detail",
  "View history": "Lihat riwayat",
  "View analytics": "Lihat analitik",
  "View detailed report": "Lihat laporan detail",
  "View all notifications": "Lihat semua notifikasi",
  "View less": "Tampilkan lebih sedikit",
  "Show less": "Tampilkan lebih sedikit",
  "Start New Preparation": "Mulai Persiapan Baru",
  "New Preparation": "Persiapan Baru",
  "Pick raw material from warehouse": "Pilih bahan baku dari gudang",
  "1. Pick Raw Material": "1. Pilih Bahan Baku",
  "Search or pick raw material from available stock": "Cari atau pilih bahan baku dari stok tersedia",
  "2. Set Output": "2. Tentukan Output",
  "Choose the ready-to-cook output": "Pilih hasil bahan siap masak",
  "3. Enter Quantity": "3. Masukkan Jumlah",
  "Input the weight to be processed": "Input berat yang akan diproses",
  "4. Start Preparation": "4. Mulai Persiapan",
  "Enter the production line by status": "Masuk ke jalur produksi sesuai status",
  "Most Frequent": "Paling Sering",
  "Prepare Now": "Siapkan Sekarang",
  "Standard Yield": "Rendemen Standar",
  "Estimated Result": "Estimasi Hasil",
  "Raw Material Stock": "Stok Bahan Baku",
  "Ready-to-Cook": "Bahan Siap Masak",
  "Favorite Ingredients": "Bahan Favorit",
  "Frequently Produced": "Sering Diproduksi",
  "Search raw material or output…": "Cari bahan baku atau hasil…",
  "Sort": "Urutkan",
  "Most stock": "Stok terbanyak",
  "View All Raw Materials": "Lihat Semua Bahan Baku",
  "Alerts & Notifications": "Peringatan & Notifikasi",
  "No alerts": "Tidak ada peringatan",
  "Today's Performance": "Performa Hari Ini",
  "Waste / Shrinkage": "Waste / Susut",
  "Number of Batches": "Jumlah Batch",
  "Efficiency": "Efisiensi",
  "batches": "batch",
  "Target": "Target",
  "In progress": "Sedang berjalan",
  "minutes ago": "menit lalu",
  "Late Preparation": "Persiapan Terlambat",
  "late by": "terlambat",
  "minutes": "menit",
  "Abnormal Yield": "Rendemen Tidak Normal",
  "standard": "standar",
  "Back to Meja Prepare": "Kembali ke Meja Prepare",
  "Convert raw materials into semi-finished — with measured yield & shrinkage": "Ubah bahan baku menjadi Barang Setengah Jadi — dengan rendemen & penyusutan terukur",
  "Locked to your kitchen warehouse": "Terkunci pada gudang dapur Anda",
  "Semi-Finished": "Barang Setengah Jadi",
  "Central Kitchen": "Dapur Pusat",
  "Branch Kitchen": "Dapur Cabang",


  // ---- Stock Transfer (Central -> Branch logistics) ----
  "Stock Transfer": "Transfer Stok",
  "Transfer Stok": "Transfer Stok",
  "Move ready-to-cook stock from Central Warehouse to a branch": "Pindahkan stok Bahan Siap Masak dari Gudang Pusat ke cabang",
  "Send to Branch": "Kirim ke Cabang",
  "In Transit": "Dalam Perjalanan",
  "Total Items Transferred": "Total Item Ditransfer",
  "Awaiting Receiving": "Menunggu Penerimaan",
  "No transfers in transit": "Tidak ada transfer dalam perjalanan",
  "Transfer History": "Riwayat Transfer",
  "No transfer history yet": "Belum ada riwayat transfer",
  "Details": "Detail",
  "Transfer cancelled & central stock restored": "Transfer dibatalkan & stok pusat dikembalikan",
  "Dispatch Transfer": "Kirim Transfer",
  "Destination Branch": "Cabang Tujuan",
  "Select branch…": "Pilih cabang…",
  "Search product to add…": "Cari produk untuk ditambah…",
  "No products available": "Tidak ada produk tersedia",
  "Central stock is deducted now. The branch confirms receiving to add it to branch inventory.": "Stok pusat langsung dikurangi sekarang. Cabang mengonfirmasi penerimaan untuk menambahkannya ke inventory cabang.",
  "Transfer dispatched": "Transfer dikirim",
  "Receive Transfer": "Terima Transfer",
  "Confirm Receiving": "Konfirmasi Penerimaan",
  "Destination": "Tujuan",
  "Sent": "Dikirim",
  "Received quantity is added to this branch's inventory and becomes available for recipes & POS.": "Jumlah yang diterima ditambahkan ke inventory cabang ini dan siap dipakai untuk resep & POS.",
  "Transfer received into branch inventory": "Transfer diterima ke inventory cabang",
  "Transfer": "Transfer",
  "Dispatched": "Dikirim",

  // ---- Operations Control Tower (Operational Cycle) ----
  "Operations Control Tower": "Menara Kendali Operasi",
  "Control Tower": "Menara Kendali",
  "Today's operational cycle": "Siklus operasi hari ini",
  "Live status of today's operational cycle across all branches": "Status langsung siklus operasi hari ini di seluruh cabang",
  "Cutoff": "Batas Waktu",
  "done": "selesai",
  "late": "terlambat",
  "Late": "Terlambat",
  "Reopened": "Dibuka Kembali",
  "Reopen": "Buka Kembali",
  "Reopen Order": "Buka Kembali Pesanan",
  "This branch missed the night cutoff. Reopening is audited.": "Cabang ini melewati batas waktu malam. Pembukaan kembali dicatat.",
  "Reason is required": "Alasan wajib diisi",
  "reopened": "dibuka kembali",
  "e.g. Branch had a network outage": "mis. Cabang mengalami gangguan jaringan",
  "Loading operations pipeline...": "Memuat alur operasi...",
  "The Control Tower is available to HQ and Owner accounts only.": "Menara Kendali hanya tersedia untuk akun Pusat dan Pemilik.",
  "Bottleneck": "Penghambat",
  "waiting on": "menunggu",
  "Branch Progress": "Progres Cabang",
  "Each branch across the 7 phases": "Setiap cabang melalui 7 fase",
  "In Progress": "Berjalan",
  "Waiting": "Menunggu",
  "Delayed": "Terlambat",
  "Branch Orders": "Pesanan Cabang",
  "Consolidation": "Ringkasan Pesanan Cabang",
  "Fresh Market": "Pasar Segar",
  "POS Sync": "Sinkron POS",
  // ---- Phase B Workspaces ----
  "Night Ops": "Operasi Malam",
  "Night Ops is for branch accounts. Select a branch from the dashboard.": "Operasi Malam untuk akun cabang. Pilih cabang dari dasbor.",
  "Tonight's Tasks": "Tugas Malam Ini",
  "Stock take, waste, staff meals & tomorrow's order — one guided checklist": "Stok, limbah, makan karyawan & pesanan besok — satu checklist terpandu",
  "Progress": "Progres",
  "required done": "wajib selesai",
  "All set for tonight!": "Semua siap malam ini!",
  "Your order has been handed over to": "Pesanan Anda telah diserahkan ke",
  "Central Purchasing": "Pembelian Pusat",
  "optional": "opsional",
  "Not done yet": "Belum dikerjakan",
  "Review": "Tinjau",
  "Open": "Buka",
  "Submit": "Kirim",
  "Next": "Berikutnya",
  "Quick Stock Take": "Stok Cepat",
  "Record Waste": "Catat Limbah",
  "Record Staff Meals": "Catat Makan Karyawan",
  "Submit Tomorrow's Order": "Kirim Pesanan Besok",
  "Purchasing Workspace": "Ruang Kerja Pembelian",
  "Demand is consolidated automatically — review and execute": "Permintaan dikonsolidasi otomatis — tinjau dan eksekusi",
  "Branches Submitted": "Cabang Terkirim",
  "orders": "pesanan",
  "Central Procurement": "Pengadaan Pusat",
  "Purchased Today": "Dibeli Hari Ini",
  "approved": "disetujui",
  "Fresh Market Queue": "Antrian Pasar Segar",
  "Central Procurement Queue": "Antrian Pengadaan Pusat",
  "items": "item",
  "No demand in this queue today": "Tidak ada permintaan di antrian ini hari ini",
  "Execute in Procurement": "Eksekusi di Procurement",
  "Central Warehouse": "Gudang Pusat",
  "Warehouse Workspace": "Ruang Kerja Gudang",
  "Receive → Pick → Pack → Delivery Note → Ready": "Terima → Picking → Packing → Surat Jalan → Siap",
  "Receive Supplier Goods": "Terima Barang Supplier",
  "purchase orders awaiting receipt": "PO menunggu penerimaan",
  "To Pick": "Untuk Picking",
  "Ready for Delivery": "Siap Kirim",
  "Start Picking": "Mulai Picking",
  "Finish → Packing": "Selesai → Packing",
  "Delivery Note & Ready": "Surat Jalan & Siap",
  "Empty": "Kosong",
  "Deliver": "Kirim",
  "Updated": "Diperbarui",
  "Fresh Market item (perishable)": "Barang Pasar Segar (mudah rusak)",
  "Goes to the Fresh Market purchasing queue": "Masuk ke antrian pembelian Pasar Segar",
  // ---- Phase C: Delivery / Receiving / Variance / Notifications ----
  "Receiving Variances to Investigate": "Selisih Penerimaan untuk Investigasi",
  "No open variances — everything reconciled": "Tidak ada selisih terbuka — semua cocok",
  "Resolve": "Selesaikan",
  "Resolve Variance": "Selesaikan Selisih",
  "Record the investigation outcome. The Owner will be notified.": "Catat hasil investigasi. Pemilik akan diberi tahu.",
  "e.g. Supplier shortfall confirmed; credit note requested": "mis. Kekurangan dari supplier dikonfirmasi; minta nota kredit",
  "Variance resolved": "Selisih diselesaikan",
  "expected": "diharapkan",
  "received": "diterima",
  "short": "kurang",
  "missing": "hilang",
  "damaged": "rusak",
  "over": "lebih",
  "inapp": "dalam aplikasi",

  // ---- Language switcher ----
  "Language": "Bahasa",
  "English": "English",
  "Indonesia": "Indonesia",

  // ---- Roles ----
  "Super Admin": "Super Admin",
  "Owner": "Pemilik",
  "Central Purchasing": "Pembelian Pusat",
  "Warehouse": "Gudang",
  "Branch Staff": "Staf Cabang",

  // ---- Navigation ----
  "Dashboard": "Dasbor",
  "Orders": "Pesanan",
  "My Inventory": "Inventaris Saya",
  "Purchasing": "Pembelian",
  "Delivery": "Pengiriman",
  "Receiving": "Penerimaan Barang",
  "Inventory": "Inventaris",
  "Procurement": "Pengadaan",
  "Recipes & BOM": "Resep & BOM",
  "Sales": "Penjualan",
  "Leakage & Variance": "Kebocoran & Selisih",
  "Stock Take": "Stok Opname",
  "Approvals": "Persetujuan",
  "Expiry": "Kadaluarsa",
  "Stock Ledger": "Riwayat Stok",
  "Waste": "Barang Rusak",
  "Branch Notes": "Catatan Cabang",
  "Daily Closing": "Tutup Harian",
  "Notifications": "Notifikasi",
  "Audit Timeline": "Riwayat Audit",
  "Master Data": "Data Master",
  "Feedback": "Masukan",
  "Users & Roles": "Pengguna & Peran",
  // mobile nav
  "Home": "Beranda",
  "Stock": "Stok",
  "Alerts": "Notifikasi",

  // ---- Layout / chrome ----
  "Procurement ERP": "ERP Pengadaan",
  "Logout": "Keluar",
  "Read-only Executive View": "Tampilan Eksekutif (Hanya Baca)",

  // ---- Statuses ----
  "Draft": "Draf",
  "Submitted": "Dikirim",
  "Approved": "Disetujui",
  "Purchased": "Dibeli",
  "Adjusting": "Penyesuaian",
  "Delivered": "Terkirim",
  "Received": "Diterima",
  "Pending": "Menunggu",
  "Pending Approval": "Menunggu Persetujuan",
  "Rejected": "Ditolak",
  "Safe": "Aman",
  "Low": "Menipis",
  "Critical": "Kritis",
  "Open": "Terbuka",
  "Resolved": "Selesai",
  "Overdue": "Terlambat",
  "Active": "Aktif",
  "archived": "diarsipkan",
  "active": "aktif",
  "superseded": "digantikan",
  // raw enums (badges)
  "EXCELLENT": "Sangat Baik",
  "GOOD": "Baik",
  "WARNING": "Peringatan",
  "CRITICAL": "Kritis",
  "LOW": "Menipis",
  "SAFE": "Aman",
  "DRAFT": "Draf",
  "SUBMITTED": "Dikirim",
  "PURCHASED": "Dibeli",
  "ADJUSTING": "Penyesuaian",
  "DELIVERED": "Terkirim",
  "RECEIVED": "Diterima",
  "PENDING": "Menunggu",
  "APPROVED": "Disetujui",
  "REJECTED": "Ditolak",
  "Waste": "Barang Rusak",

  // ---- Stock status labels ----
  "Low Stock": "Stok Menipis",
  "Critical Stock": "Stok Kritis",
  "Expiring Soon": "Segera Kadaluarsa",
  "Expired": "Kadaluarsa",

  // ---- Waste categories ----
  "Spoiled": "Busuk",
  "Customer Complaint": "Komplain Pelanggan",
  "Kitchen Error": "Kesalahan Dapur",
  "Missing / Lost": "Hilang",
  "Broken Packaging": "Kemasan Rusak",

  // ---- Common buttons / actions ----
  "Save": "Simpan",
  "Cancel": "Batal",
  "Submit": "Kirim",
  "Confirm": "Konfirmasi",
  "Close": "Tutup",
  "Back": "Kembali",
  "Delete": "Hapus",
  "Edit": "Ubah",
  "Add": "Tambah",
  "Search": "Cari",
  "Approve": "Setujui",
  "Reject": "Tolak",
  "Save Draft": "Simpan Draf",
  "Draft": "Draf",
  "All": "Semua",
  "View all": "Lihat semua",
  "Loading…": "Memuat…",
  "Loading...": "Memuat...",

  // ---- Login ----
  "Sign in": "Masuk",
  "Sign In": "Masuk",
  "Access your operations dashboard.": "Akses dasbor operasional Anda.",
  "Email": "Email",
  "Password": "Kata Sandi",
  "Welcome back!": "Selamat datang kembali!",
  "Login failed": "Gagal masuk",
  "Quick demo logins": "Login demo cepat",
  "HRD Portal": "Portal HRD",
  "Sign in with Employee ID & PIN": "Masuk dengan Employee ID & PIN",
  "One kitchen command center for every branch.": "Satu pusat kendali dapur untuk semua cabang.",
  "Procurement, purchasing, warehouse, delivery, inventory and waste — centralized and beautifully simple.":
    "Pengadaan, pembelian, gudang, pengiriman, inventaris, dan barang rusak — terpusat dan sangat sederhana.",

  // ---- Branch Dashboard ----
  "Your branch operations at a glance": "Ringkasan operasi cabang Anda",
  "Inventory Health": "Kesehatan Inventaris",
  "Accuracy": "Akurasi",
  "Last Count": "Hitungan Terakhir",
  "Never": "Belum pernah",
  "New Order": "Pesanan Baru",
  "Receive": "Terima",
  "Notes": "Catatan",
  "Stock take overdue": "Stok opname terlambat",
  "No stock take recorded yet": "Belum ada stok opname",
  "Count now": "Hitung sekarang",
  "Last counted": "Terakhir dihitung",
  "day(s) ago": "hari lalu",
  "target every": "target setiap",
  "days": "hari",
  "Pending Receiving": "Menunggu Penerimaan",
  "Open Stock Take": "Stok Opname Terbuka",
  "Urgent Orders": "Pesanan Mendesak",
  "Waste Pending": "Barang Rusak Menunggu",
  "Open Notes": "Catatan Terbuka",
  "Needs Attention": "Perlu Perhatian",
  "All stock levels are healthy.": "Semua level stok sehat.",
  "Nothing expiring soon.": "Tidak ada yang segera kadaluarsa.",
  "Awaiting Receiving": "Menunggu Penerimaan",
  "Scoped to": "Terbatas pada",
  "your branch": "cabang Anda",
  "only": "saja",
  "Unable to load your branch dashboard.": "Tidak dapat memuat dasbor cabang Anda.",
  "My Branch": "Cabang Saya",

  // ---- Branch Inventory ----
  "My Branch Inventory": "Inventaris Cabang Saya",
  "Your branch stock, movements & expiry": "Stok, pergerakan & kadaluarsa cabang Anda",
  "Search products...": "Cari produk...",
  "No products match.": "Tidak ada produk yang cocok.",
  "Movement History": "Riwayat Pergerakan",
  "No movements recorded yet.": "Belum ada pergerakan tercatat.",
  "History": "Riwayat",
  "Min": "Min",
  "Ideal": "Ideal",
  "Fresh": "Segar",
  "Exp ≤7d": "Kad ≤7h",
  "Exp ≤30d": "Kad ≤30h",

  // ---- Orders ----
  "Branch Orders": "Pesanan Cabang",
  "Create, submit and track branch orders": "Buat, kirim, dan lacak pesanan cabang",
  "Branch order forms — submit, track and receive": "Form pesanan cabang — kirim, lacak, dan terima",
  "All Orders": "Semua Pesanan",
  "No orders yet": "Belum ada pesanan",
  "No orders yet.": "Belum ada pesanan.",
  "Start by creating a new branch order. It's quick and fun!": "Mulai dengan membuat pesanan cabang baru. Cepat dan mudah!",
  "No orders match the selected filters.": "Tidak ada pesanan yang cocok dengan filter.",
  "Today": "Hari Ini",
  "Yesterday": "Kemarin",
  "This Week": "Minggu Ini",
  "This Month": "Bulan Ini",
  "Custom": "Kustom",
  "to": "ke",
  "order": "pesanan",
  "orders": "pesanan",
  "Special": "Spesial",
  "+ added": "+ tambahan",
  "Edit Draft": "Ubah Draf",
  "Invoice": "Faktur",
  "Notes": "Catatan",
  "Notes: ": "Catatan: ",
  "Order": "Pesanan",
  "items": "item",
  "Product": "Produk",
  "Ordered": "Dipesan",
  "Delivered": "Terkirim",
  "Status": "Status",
  "Branch": "Cabang",
  "Order Date": "Tanggal Pesan",
  "Requested Delivery": "Permintaan Pengiriman",
  "Created By": "Dibuat Oleh",
  "Created At": "Dibuat Pada",
  "Last Updated": "Terakhir Diperbarui",
  "Edit": "Ubah",
  "View": "Lihat",

  // ---- Order Form (POS) ----
  "Review & Confirm": "Tinjau & Konfirmasi",
  "Submit Order": "Kirim Pesanan",
  "Save as Draft": "Simpan sebagai Draf",
  "Submit": "Kirim",
  "Back to products": "Kembali ke produk",
  "Check everything below, then save as draft or submit for approval.": "Periksa semuanya di bawah, lalu simpan draf atau kirim untuk persetujuan.",
  "Order Items": "Item Pesanan",
  "item": "item",
  "Order Tracking": "Pelacakan Pesanan",
  "Summary": "Ringkasan",
  "Total Items": "Total Item",
  "Total Quantity": "Total Jumlah",
  "Estimated Value": "Perkiraan Nilai",
  "Editing": "Mengubah",
  "New Order · number assigned on save": "Pesanan Baru · nomor diberikan saat disimpan",
  "Number assigned on save": "Nomor diberikan saat disimpan",
  "Order Date (created)": "Tanggal Pesan (dibuat)",
  "Order Time (created)": "Waktu Pesan (dibuat)",
  "Ordered By": "Dipesan Oleh",
  "Order Type": "Tipe Pesanan",
  "Not specified": "Tidak ditentukan",
  "Created Date": "Tanggal Dibuat",
  "Created Time": "Waktu Dibuat",
  "Last Updated By": "Terakhir Diperbarui Oleh",
  "Regular Order": "Pesanan Reguler",
  "Urgent Order": "Pesanan Mendesak",
  "Special Request": "Permintaan Khusus",
  "All Products": "Semua Produk",
  "Optional note for this order...": "Catatan opsional untuk pesanan ini...",
  "Search products instantly...": "Cari produk secara instan...",
  "No products match your search.": "Tidak ada produk yang cocok.",
  "Your Order": "Pesanan Anda",
  "Tap products to add them here": "Ketuk produk untuk menambahkannya",
  "Suggested": "Disarankan",
  "Use": "Pakai",
  "Passthrough": "Passthrough",
  "per": "per",
  "Add at least one product": "Tambahkan minimal satu produk",
  "Order submitted for approval!": "Pesanan dikirim untuk persetujuan!",
  "Failed to save": "Gagal menyimpan",
  "Review Order": "Tinjau Pesanan",
  "Cart": "Keranjang",
  "Your cart is empty": "Keranjang Anda kosong",
  "Regular": "Reguler",
  "Urgent": "Mendesak",
  "Delivery Date": "Tanggal Pengiriman",
  "Delivery Time": "Waktu Pengiriman",
  "Notes (optional)": "Catatan (opsional)",
  "All Categories": "Semua Kategori",
  "In stock": "Tersedia",
  "Order submitted": "Pesanan dikirim",
  "Draft saved": "Draf disimpan",
  "items in cart": "item di keranjang",
  "Review": "Tinjau",

  // ---- Receiving ----
  "Inspect, receive in full or partial, attach photo evidence": "Periksa, terima penuh atau sebagian, lampirkan bukti foto",
  "No deliveries awaiting confirmation.": "Tidak ada pengiriman menunggu konfirmasi.",
  "Adjust the received quantity for any item that arrived short, damaged or missing. Branch stock updates with the received amounts.": "Sesuaikan jumlah yang diterima untuk item yang kurang, rusak, atau hilang. Stok cabang diperbarui sesuai jumlah diterima.",
  "Received with": "Diterima dengan",
  "discrepancy item(s)": "item selisih",
  "Receive": "Terima",
  "Inspect & Receive": "Periksa & Terima",
  "Receive All": "Terima Semua",
  "Confirm Receiving": "Konfirmasi Penerimaan",
  "Order received in full": "Pesanan diterima penuh",
  "Expected": "Diharapkan",
  "Issue": "Masalah",
  "Short": "Kurang",
  "Missing": "Hilang",
  "Damaged": "Rusak",
  "Photo Evidence (optional)": "Bukti Foto (opsional)",
  "Add photos": "Tambah foto",
  "Notes (optional)": "Catatan (opsional)",
  "Condition, packaging, remarks...": "Kondisi, kemasan, keterangan...",

  // ---- Stock Take (Opname) ----
  // ---- Stock Take Types & Owner Authorization (Go-Live architecture) ----
  "Stock Take Type": "Tipe Stok Opname",
  "Regular Stock Take": "Stok Opname Regular",
  "First Opening Stock Take": "Stok Awal (First Opening)",
  "Correction Reset Stock Take": "Correction Reset",
  "Daily / monthly operations. No authorization required.": "Operasional harian/bulanan. Tanpa otorisasi.",
  "One-time opening count before Go-Live. Requires Owner PIN.": "Hitung stok awal 1x per lokasi sebelum Go-Live. Perlu PIN Owner.",
  "Already executed for this location (one per location).": "Sudah pernah dijalankan untuk lokasi ini (hanya 1x per lokasi).",
  "Creates a new inventory baseline. History stays untouched. Requires Owner PIN + reason.": "Membuat baseline persediaan baru. Riwayat & laporan historis tidak diubah. Perlu PIN Owner + alasan.",
  "Reason (required)": "Alasan (wajib)",
  "Reason (optional)": "Alasan (opsional)",
  "e.g. inventory accuracy became unreliable": "cth: akurasi stok sudah tidak dapat diandalkan",
  "Reason is required for a Correction Reset": "Alasan wajib diisi untuk Correction Reset",
  "Unit Cost (HPP)": "Harga Satuan (HPP)",
  "Owner Authorization Required": "Otorisasi Owner Diperlukan",
  "Enter Owner PIN": "Masukkan PIN Owner",
  "Authorize": "Otorisasi",
  "Finalizing a First Opening Stock Take requires the Owner PIN. This becomes the official opening inventory valuation.": "Finalisasi Stok Awal memerlukan PIN Owner. Hasilnya menjadi valuasi persediaan awal resmi.",
  "Finalizing a Correction Reset requires the Owner PIN. A new inventory baseline is created — history stays untouched.": "Finalisasi Correction Reset memerlukan PIN Owner. Baseline persediaan baru dibuat — riwayat tetap utuh.",
  "First Opening establishes the official opening inventory valuation. Unit cost (HPP) is editable here. Finalizing requires the Owner PIN.": "Stok Awal menetapkan valuasi persediaan awal resmi. Harga satuan (HPP) dapat diedit di sini. Finalisasi memerlukan PIN Owner.",
  "Correction Reset creates a NEW inventory baseline as of today. Historical journals and reports are never modified. Finalizing requires the Owner PIN.": "Correction Reset membuat baseline persediaan BARU per hari ini. Jurnal & laporan historis tidak pernah diubah. Finalisasi memerlukan PIN Owner.",
  "Inventory Baseline Record (Permanent)": "Catatan Baseline Persediaan (Permanen)",
  "Baseline Version": "Versi Baseline",
  "Effective Date": "Tanggal Efektif",
  "Previous Inventory Value": "Nilai Persediaan Sebelumnya",
  "New Inventory Value": "Nilai Persediaan Baru",
  "Submitted By": "Diajukan Oleh",
  "Authorization": "Otorisasi",
  "Count your branch stock vs the system": "Hitung stok cabang Anda vs sistem",
  "Physical stock count vs system — central & branch": "Hitungan stok fisik vs sistem — pusat & cabang",
  "New Stock Take": "Stok Opname Baru",
  "No stock takes yet. Start one to reconcile physical stock.": "Belum ada stok opname. Mulai satu untuk mencocokkan stok fisik.",
  "Start Stock Take": "Mulai Stok Opname",
  "Start Count": "Mulai Hitung",
  "Save Counts": "Simpan Hitungan",
  "Counts saved": "Hitungan disimpan",
  "Finalize": "Finalisasi",
  "FINALIZED": "DIFINALISASI",
  "started": "dimulai",
  "Finalized —": "Difinalisasi —",
  "applied": "diterapkan",
  "sent for approval": "dikirim untuk persetujuan",
  "Actual Count": "Hitungan Aktual",
  "line(s) awaiting HQ approval.": "baris menunggu persetujuan HQ.",
  "Lines within ±5% variance adjust stock automatically. Lines over ±5% require HQ approval before stock changes.": "Baris dengan selisih ±5% menyesuaikan stok otomatis. Baris di atas ±5% perlu persetujuan HQ sebelum stok berubah.",
  "Finalize this stock take? Lines within ±5% variance auto-adjust stock. Lines over ±5% are sent to HQ for approval before adjusting.": "Finalisasi stok opname ini? Baris dengan selisih ±5% menyesuaikan stok otomatis. Baris di atas ±5% dikirim ke HQ untuk persetujuan sebelum disesuaikan.",
  "Finalize this opname? Stock will be adjusted and ledger entries created.": "Finalisasi stok opname ini? Stok akan disesuaikan dan entri riwayat dibuat.",
  "pending": "menunggu",
  "This counts your own branch inventory.": "Ini menghitung inventaris cabang Anda sendiri.",
  "A snapshot of current system quantities will be captured. Enter physical counts, then finalize.": "Snapshot jumlah sistem saat ini akan diambil. Masukkan hitungan fisik, lalu finalisasi.",
  "Variances over ±5% are sent to HQ for approval.": "Selisih di atas ±5% dikirim ke HQ untuk persetujuan.",
  "Adjustments + ledger entries are generated automatically.": "Penyesuaian + entri riwayat dibuat otomatis.",
  "Select branch...": "Pilih cabang...",
  "Start Count": "Mulai Hitung",
  "Scope": "Cakupan",
  "System": "Sistem",
  "Actual": "Aktual",
  "Difference": "Selisih",
  "Reason": "Alasan",

  // ---- Waste ----
  "Waste Management": "Manajemen Barang Rusak",
  "Track and value wasted stock": "Lacak dan nilai stok yang rusak",
  "Report Waste": "Laporkan Barang Rusak",
  "Quantity": "Jumlah",
  "Category": "Kategori",
  "Notes": "Catatan",
  "Photo Evidence": "Bukti Foto",
  "A reason is required for every waste report": "Alasan wajib untuk setiap laporan barang rusak",
  "A photo is required as waste evidence": "Foto wajib sebagai bukti barang rusak",
  "Waste reported & stock reduced": "Barang rusak dilaporkan & stok dikurangi",
  "Select product and quantity": "Pilih produk dan jumlah",
  "Waste submitted — pending HQ approval (over threshold)": "Barang rusak dikirim — menunggu persetujuan HQ (di atas ambang)",
  "Total waste value recorded": "Total nilai barang rusak tercatat",
  "No waste recorded": "Belum ada barang rusak",
  "Great job keeping waste low! Report any waste here to keep stock accurate.": "Bagus! Laporkan barang rusak di sini agar stok tetap akurat.",
  "by": "oleh",
  "Select product...": "Pilih produk...",
  "Why is this being wasted?": "Mengapa ini dibuang/rusak?",
  "Waste over": "Barang rusak di atas",
  "or more than": "atau lebih dari",
  "of current branch stock needs HQ approval before stock is reduced.": "dari stok cabang perlu persetujuan HQ sebelum stok dikurangi.",
  "Report": "Laporkan",

  // ---- Branch Notes ----
  "Operational, equipment & supply issues": "Masalah operasional, peralatan & pasokan",
  "New Note": "Catatan Baru",
  "No notes here": "Tidak ada catatan di sini",
  "Operational": "Operasional",
  "Equipment": "Peralatan",
  "Supply": "Pasokan",
  "Equipment Issue": "Masalah Peralatan",
  "Supply Issue": "Masalah Pasokan",
  "Priority": "Prioritas",
  "Title (optional)": "Judul (opsional)",
  "Message": "Pesan",
  "New Branch Note": "Catatan Cabang Baru",
  "Log equipment problems, supply issues or operational notes so HQ can act on them.": "Catat masalah peralatan, pasokan, atau operasional agar HQ dapat menindaklanjuti.",
  "By": "Oleh",
  "Type": "Tipe",
  "Low": "Rendah",
  "Normal": "Normal",
  "High": "Tinggi",
  "Describe the issue or note...": "Jelaskan masalah atau catatan...",
  "Photo (optional)": "Foto (opsional)",
  "A message is required": "Pesan wajib diisi",
  "Note submitted": "Catatan dikirim",
  "Resolve": "Selesaikan",
  "Reopen": "Buka Lagi",
  "HIGH": "TINGGI",
  "NORMAL": "NORMAL",
  "LOW": "RENDAH",

  // ---- Sales ----
  "Daily sales by menu item — manual or Excel/CSV import": "Penjualan harian per menu — manual atau impor Excel/CSV",
  "Import": "Impor",
  "Add Sale": "Tambah Penjualan",
  "Menu item and quantity required": "Menu dan jumlah wajib diisi",
  "Select a branch": "Pilih cabang",
  "Sale recorded": "Penjualan dicatat",
  "Download failed": "Gagal mengunduh",
  "Could not parse file": "Gagal membaca file",
  "No valid rows to import": "Tidak ada baris valid untuk diimpor",
  "Imported": "Diimpor",
  "sales row(s)": "baris penjualan",
  "Delete this sales row?": "Hapus baris penjualan ini?",
  "No sales recorded": "Belum ada penjualan tercatat",
  "Add sales manually or import your daily sales report to enable consumption & variance analysis.": "Tambah penjualan manual atau impor laporan penjualan harian untuk analisis konsumsi & selisih.",
  "Select branch…": "Pilih cabang…",
  "Select menu item…": "Pilih menu…",
  "Quantity Sold": "Jumlah Terjual",
  "Gross Sales (Rp, optional)": "Penjualan Bruto (Rp, opsional)",
  "Import Sales": "Impor Penjualan",
  "Upload a CSV or Excel file with columns:": "Unggah file CSV atau Excel dengan kolom:",
  "Gross optional": "Bruto opsional",
  "Your branch is applied automatically.": "Cabang Anda diterapkan otomatis.",
  "Choose CSV / XLSX file": "Pilih file CSV / XLSX",
  "valid row(s)": "baris valid",
  "Menu": "Menu",
  "manual": "manual",
  "import": "impor",
  "All branches": "Semua cabang",
  "Units sold": "Unit terjual",
  "Gross": "Bruto",
  "No sales recorded": "Belum ada penjualan tercatat",
  "Menu Item": "Menu",
  "Qty": "Jml",
  "Source": "Sumber",
  "Date": "Tanggal",
  "Quantity Sold": "Jumlah Terjual",
  "Gross Sales (Rp, optional)": "Penjualan Bruto (Rp, opsional)",
  "Sale recorded": "Penjualan dicatat",
  "Import Sales": "Impor Penjualan",
  "Download template": "Unduh templat",
  // ---- Sales Import Wizard (Phase 1) ----
  "Sales Import Wizard": "Wizard Impor Penjualan",
  "Map Columns": "Petakan Kolom",
  "Preview": "Pratinjau",
  "Done": "Selesai",
  "Upload": "Unggah",
  "Wizard (any POS export)": "Wizard (ekspor POS apa pun)",
  "Central Flow Template": "Template Central Flow",
  "Upload any POS export (CSV, XLS, XLSX). We'll detect the columns automatically.": "Unggah ekspor POS apa pun (CSV, XLS, XLSX). Kolom akan dideteksi otomatis.",
  "Choose CSV / XLS / XLSX file": "Pilih file CSV / XLS / XLSX",
  "Reading file…": "Membaca file…",
  "Could not read the file": "Tidak dapat membaca file",
  "Template found:": "Template ditemukan:",
  "Use Template": "Gunakan Template",
  "Manual Mapping": "Pemetaan Manual",
  "Duplicate column names detected:": "Nama kolom ganda terdeteksi:",
  "Map each Central Flow field to a column from your file.": "Petakan setiap kolom Central Flow ke kolom dari file Anda.",
  "rows detected": "baris terdeteksi",
  "Detected": "Terdeteksi",
  "rows": "baris",
  "columns": "kolom",
  "No column headers detected": "Tidak ada header kolom terdeteksi",
  "Re-upload": "Unggah Ulang",
  "Sample": "Contoh",
  "Parsed Date": "Tanggal Terbaca",
  "Original Value": "Nilai Asli",
  // ---- Unknown Menu Mapping Engine ----
  "Unknown Menu Detected": "Menu Tidak Dikenal Terdeteksi",
  "Match each POS menu name to a Central Flow menu.": "Cocokkan setiap nama menu POS ke menu Central Flow.",
  "(Ask an admin to save mappings.)": "(Minta admin untuk menyimpan pemetaan.)",
  "Select Central Flow Menu": "Pilih Menu Central Flow",
  "Remember mapping — save for future imports": "Ingat pemetaan — simpan untuk impor berikutnya",
  "Apply Mapping": "Terapkan Pemetaan",
  "Mappings saved": "Pemetaan disimpan",
  "Select a menu for at least one item": "Pilih menu untuk setidaknya satu item",
  "row(s) auto-mapped from saved menu mappings": "baris dipetakan otomatis dari pemetaan tersimpan",
  "row(s)": "baris",
  // ---- Step 5D: Menu Master Import Wizard ----
  "Import Menu": "Impor Menu",
  "Menu Import Wizard": "Wizard Impor Menu",
  "Menu Import History": "Riwayat Impor Menu",
  "Bulk-create menu master records from a POS export. Creates menus only — no recipes, BOM, inventory, or sales.": "Buat master menu massal dari ekspor POS. Hanya membuat menu — tanpa resep, BOM, inventaris, atau penjualan.",
  "Total Rows": "Total Baris",
  "New Menus": "Menu Baru",
  "Existing": "Sudah Ada",
  "Invalid": "Tidak Valid",
  "Existing menus (matched by code/name):": "Menu yang sudah ada (cocok kode/nama):",
  "Skip Existing": "Lewati yang Ada",
  "Update Existing": "Perbarui yang Ada",
  "menu(s) match an existing name": "menu cocok dengan nama yang sudah ada",
  "Menu Name": "Nama Menu",
  "Menu Code": "Kode Menu",
  "Selling Price": "Harga Jual",
  "Description": "Deskripsi",
  "Updated": "Diperbarui",
  "Rollback": "Batalkan",
  "Rolled back": "Dibatalkan",
  "Roll back this import? Created menus will be removed and updates reverted.": "Batalkan impor ini? Menu yang dibuat akan dihapus dan perubahan dikembalikan.",
  "removed": "dihapus",
  "restored": "dikembalikan",
  "Optional": "Opsional",
  "Mandatory": "Wajib",
  "Choose a column…": "Pilih kolom…",
  "— none —": "— tidak ada —",
  "Save this mapping as a template": "Simpan pemetaan ini sebagai template",
  "e.g. Moka Export, Majoo Export": "mis. Moka Export, Majoo Export",
  "Template saved": "Template disimpan",
  "Next: Preview": "Lanjut: Pratinjau",
  "Row Count": "Jumlah Baris",
  "Total Qty": "Total Qty",
  "Estimated Sales": "Estimasi Penjualan",
  "Hide errors": "Sembunyikan error",
  "View errors": "Lihat error",
  "Showing first 20 rows": "Menampilkan 20 baris pertama",
  "Row": "Baris",
  "Import Complete": "Impor Selesai",
  "Skipped": "Dilewati",
  "Errors": "Error",
  "Gross Sales": "Penjualan Bruto",
  "Import History": "Riwayat Impor",
  "No import history yet": "Belum ada riwayat impor",
  "When": "Waktu",
  "File": "Berkas",
  "By": "Oleh",
  // ---- Step 5A: Sales Consumption (Auto BOM) ----
  "Ingredients Consumed": "Bahan Terpakai",
  "Branch ingredient stock was automatically reduced based on each menu's recipe (BOM).": "Stok bahan cabang otomatis berkurang berdasarkan resep (BOM) tiap menu.",
  "Missing Recipe — not consumed:": "Resep Hilang — tidak dikonsumsi:",
  "Sales Consumption": "Konsumsi Penjualan",
  "Consumption": "Konsumsi",
  "Sales Consumption Summary": "Ringkasan Konsumsi Penjualan",
  "All Branches": "Semua Cabang",
  "Products": "Produk",
  "Est. Cost Value": "Estimasi Nilai Biaya",
  "Est. Value": "Estimasi Nilai",
  "Consumed Qty": "Qty Terpakai",
  "units": "unit",
  "menu item(s) sold without a recipe were not consumed": "menu terjual tanpa resep tidak dikonsumsi",
  "No sales consumption in this period": "Tidak ada konsumsi penjualan pada periode ini",
  "Production Use": "Pemakaian Produksi",
  "Production Output": "Hasil Produksi",
  // ---- Step 5B: Employee Meal (Konsumsi Karyawan) ----
  "Employee Meal": "Konsumsi Karyawan",
  "Staff meals — legitimate internal consumption": "Konsumsi makan staf — pemakaian internal yang sah",
  "Record Meal": "Catat Konsumsi",
  "Record Employee Meal": "Catat Konsumsi Karyawan",
  "Employee meal recorded": "Konsumsi karyawan dicatat",
  "No employee meals yet": "Belum ada konsumsi karyawan",
  "Record staff meals to track internal consumption.": "Catat konsumsi staf untuk melacak pemakaian internal.",
  "staff": "karyawan",
  "Number of Employees": "Jumlah Karyawan",
  "Consumption Items": "Item Konsumsi",
  "Add Item": "Tambah Item",
  "Search product…": "Cari produk…",
  "No products with stock": "Tidak ada produk dengan stok",
  "Select a branch": "Pilih cabang",
  "avail": "tersedia",
  "Stok tidak mencukupi": "Stok tidak mencukupi",
  "e.g. Lunch shift": "mis. Shift makan siang",
  "Employee Meal Report": "Laporan Konsumsi Karyawan",
  "Employee Meal This Month": "Konsumsi Karyawan Bulan Ini",
  "Legitimate staff consumption — not waste or leakage": "Konsumsi staf yang sah — bukan barang rusak atau kebocoran",
  "Employees Fed": "Karyawan Dilayani",
  "Employee Meal Cost Trend": "Tren Biaya Konsumsi Karyawan",
  "Employee Count Trend": "Tren Jumlah Karyawan",
  "No employee meals in this period": "Tidak ada konsumsi karyawan pada periode ini",
  "Total Cost": "Total Biaya",
  "Choose CSV / XLSX file": "Pilih file CSV / XLSX",
  "Parsing…": "Memproses…",
  "valid": "valid",
  "invalid": "tidak valid",

  // ---- Leakage & Variance ----
  "Expected vs actual usage between stock takes — unexplained loss": "Pemakaian aktual vs perkiraan antar stok opname — kehilangan tak terjelaskan",
  "Company Leakage": "Kebocoran Perusahaan",
  "Suspicious Items": "Item Mencurigakan",
  "Branches Analyzed": "Cabang Dianalisis",
  "Branch Leakage Ranking": "Peringkat Kebocoran Cabang",
  "Best = least leakage": "Terbaik = paling sedikit bocor",
  "Expected Usage Value": "Nilai Pemakaian Perkiraan",
  "Actual Usage Value": "Nilai Pemakaian Aktual",
  "Leakage Value": "Nilai Kebocoran",
  "Leakage %": "Kebocoran %",
  "Ingredient Variance": "Selisih Bahan",
  "Ingredient": "Bahan",
  "Opening": "Awal",
  "Closing": "Akhir",
  "Top Missing / Loss": "Kehilangan Terbesar",
  "Top Waste": "Barang Rusak Terbesar",
  "Flag": "Tanda",
  "Warning": "Peringatan",
  "Severe": "Parah",
  "Variance analysis needs at least two finalized stock takes for this branch.":
    "Analisis selisih butuh minimal dua stok opname final untuk cabang ini.",
  "No stock-take pair": "Tidak ada pasangan stok opname",
  "No positive variance items.": "Tidak ada item selisih positif.",
  "No waste recorded in this period.": "Tidak ada barang rusak pada periode ini.",
  "Leakage & Variance": "Kebocoran & Selisih",
  "Leakage threshold updated": "Ambang kebocoran diperbarui",
  "Flag > Rp": "Tandai > Rp",
  "flagged": "ditandai",
  "no data": "tanpa data",
  "off-recipe": "di luar resep",
  "Actual": "Aktual",
  "Expected": "Perkiraan",
  "Leakage": "Kebocoran",

  // ---- Recipes ----
  "Recipes & BOM": "Resep & BOM",
  "Menu items and their ingredient recipes (versioned)": "Menu dan resep bahannya (berversi)",
  "Menu Item": "Menu",
  "No menu items yet": "Belum ada menu",
  "Recipe": "Resep",
  "No recipe": "Tanpa resep",
  "Edit Recipe": "Ubah Resep",
  "Add Recipe": "Tambah Resep",
  "Version history": "Riwayat versi",
  "Save Recipe": "Simpan Resep",
  "Recipe cost": "Biaya resep",

  // ---- generic ----
  "Cancel": "Batal",
  "Save": "Simpan",
  "Failed": "Gagal",
  "Deleted": "Dihapus",
  "Updated": "Diperbarui",

  // ================= BATCH 2 — HQ SCREENS =================
  // ---- Owner Dashboard ----
  "Executive overview across all branches": "Ringkasan eksekutif seluruh cabang",
  "Total Orders": "Total Pesanan",
  "Purchasing Value": "Nilai Pembelian",
  "Waste Value": "Nilai Barang Rusak",
  "Active Branches": "Cabang Aktif",
  "Inventory Accuracy": "Akurasi Stok",
  "Waste %": "Barang Rusak %",
  "Variance %": "Selisih %",
  "Branch Inventory Health Score": "Skor Kesehatan Inventaris Cabang",
  "Branch Accuracy Ranking": "Peringkat Akurasi Cabang",
  "Latest stock take": "Stok opname terakhir",
  "Branch Performance Trends": "Tren Kinerja Cabang",
  "No stock-take history yet for this branch. Trends appear after each completed stock take.": "Belum ada riwayat stok opname untuk cabang ini. Tren muncul setelah setiap stok opname selesai.",
  "Stock take every": "Stok opname setiap",
  "overdue": "terlambat",
  "Health": "Kesehatan",
  "Stock Value": "Nilai Stok",
  "Critical": "Kritis",
  "Receiving": "Penerimaan",
  "Expired": "Kadaluarsa",
  "Last count": "Hitungan terakhir",
  "Central Inventory Health": "Kesehatan Inventaris Pusat",
  "Branch Inventory Health": "Kesehatan Inventaris Cabang",
  "Lowest Stock Items": "Item Stok Terendah",
  "Reorder Suggestions": "Saran Pemesanan",
  "Alert Center": "Pusat Peringatan",
  "Pending Approvals": "Menunggu Persetujuan",
  "Open Stock Takes": "Stok Opname Terbuka",
  "Across": "Mencakup",
  "branches": "cabang",
  "records": "catatan",
  // ---- Approvals ----
  "Stock-take variance (>5%) & high-value waste need HQ sign-off": "Selisih stok opname (>5%) & barang rusak bernilai tinggi perlu persetujuan HQ",
  "Stock Adjustments": "Penyesuaian Stok",
  "No": "Tidak ada",
  "adjustment requests.": "permintaan penyesuaian.",
  "Source": "Sumber",
  "Stock Take": "Stok Opname",
  "Manual": "Manual",
  "Current → Requested": "Saat ini → Diminta",
  "Requested By": "Diminta Oleh",
  "Action": "Aksi",
  "Adjustment approved & applied": "Penyesuaian disetujui & diterapkan",
  "Request rejected": "Permintaan ditolak",
  "Waste approved & stock reduced": "Barang rusak disetujui & stok dikurangi",
  "Waste rejected": "Barang rusak ditolak",
  "No waste reports awaiting approval.": "Tidak ada laporan barang rusak menunggu persetujuan.",
  "Central": "Pusat",
  // ---- Recipes ----
  "Menu items and their ingredient recipes (versioned)": "Menu dan resep bahannya (berversi)",
  "No menu items yet": "Belum ada menu",
  "Create sellable menu items and define their recipes to power consumption & variance analysis.": "Buat menu yang dijual dan tentukan resepnya untuk analisis konsumsi & selisih.",
  "Menu Item": "Menu",
  "Edit Menu Item": "Ubah Menu",
  "New Menu Item": "Menu Baru",
  "Menu item updated": "Menu diperbarui",
  "Menu item created": "Menu dibuat",
  "Archived (has sales)": "Diarsipkan (ada penjualan)",
  "Name is required": "Nama wajib diisi",
  "Name": "Nama",
  "Code": "Kode",
  "Sell Price (Rp)": "Harga Jual (Rp)",
  "ingredient(s)": "bahan",
  "Edit Recipe": "Ubah Resep",
  "Add Recipe": "Tambah Resep",
  "Add at least one ingredient with a quantity": "Tambahkan minimal satu bahan dengan jumlah",
  "Recipe saved": "Resep disimpan",
  "Quantity of each ingredient used per": "Jumlah tiap bahan yang dipakai per",
  "1 unit sold": "1 unit terjual",
  "Hide": "Sembunyikan",
  "Show": "Tampilkan",
  "yield/loss": "yield/susut",
  "Select ingredient…": "Pilih bahan…",
  "Add ingredient": "Tambah bahan",
  "Recipe cost": "Biaya resep",
  "Margin vs price": "Margin vs harga",
  "Save Recipe": "Simpan Resep",
  "No recipe": "Tanpa resep",
  // ---- Inventory (central) ----
  "Central & branch stock levels": "Level stok pusat & cabang",
  "Central Warehouse": "Gudang Pusat",
  "Current Stock": "Stok Saat Ini",
  "Minimum": "Minimum",
  "Ideal": "Ideal",
  "Adjust": "Sesuaikan",
  "Adjust Stock": "Sesuaikan Stok",
  "New quantity": "Jumlah baru",
  "Stock adjusted": "Stok disesuaikan",
  "Pending Stock-in": "Menunggu Stok Masuk",
  "Confirm Stock-in": "Konfirmasi Stok Masuk",
  // ---- Ledger ----
  "Every stock movement, fully traceable": "Setiap pergerakan stok, dapat dilacak penuh",
  "All movements": "Semua pergerakan",
  "Date/Time": "Tanggal/Waktu",
  "Type": "Tipe",
  "Movement": "Pergerakan",
  "Balance": "Saldo",
  "Reference": "Referensi",
  "In": "Masuk",
  "Out": "Keluar",
  "No movements found.": "Tidak ada pergerakan.",
  // ---- Procurement ----
  "Suppliers, prices, purchase orders & cost analytics": "Pemasok, harga, pesanan pembelian & analitik biaya",
  "Suppliers": "Pemasok",
  "Supplier": "Pemasok",
  "Purchase Orders": "Pesanan Pembelian",
  "Price History": "Riwayat Harga",
  "Cost Analytics": "Analitik Biaya",
  // ---- Notifications ----
  "WhatsApp & system notifications": "Notifikasi WhatsApp & sistem",
  "Mark all read": "Tandai semua dibaca",
  "No notifications": "Tidak ada notifikasi",
  "Send Queued": "Kirim Antrian",
  // ---- Audit ----
  "Audit Timeline": "Riwayat Audit",
  "Full activity history": "Riwayat aktivitas lengkap",
  "No activity yet.": "Belum ada aktivitas.",
  // ---- Users ----
  "Users & Roles": "Pengguna & Peran",
  "Manage staff accounts and permissions": "Kelola akun staf dan izin",
  "Add User": "Tambah Pengguna",
  "Role": "Peran",
  "Active": "Aktif",
  "Inactive": "Nonaktif",
  // ---- Users & Permissions (Dynamic Roles) ----
  "Manage platform access": "Kelola akses platform",
  "New User": "Pengguna Baru",
  "Edit User": "Ubah Pengguna",
  "Name": "Nama",
  "Email": "Email",
  "Used to sign in": "Digunakan untuk masuk",
  "Password": "Kata Sandi",
  "Leave blank to keep current password": "Kosongkan untuk mempertahankan kata sandi",
  "Branch": "Cabang",
  "Last Login": "Login Terakhir",
  "Status": "Status",
  "Scope": "Cakupan",
  "Never": "Belum pernah",
  "Select branch...": "Pilih cabang...",
  "User created": "Pengguna dibuat",
  "User updated": "Pengguna diperbarui",
  "User deleted": "Pengguna dihapus",
  "Fill all required fields": "Lengkapi semua kolom wajib",
  "Failed": "Gagal",
  "Create": "Buat",
  "Save": "Simpan",
  "Cancel": "Batal",
  "Edit": "Ubah",
  "Delete": "Hapus",
  "Actions": "Aksi",
  "Role Template": "Templat Peran",
  "Choose a template...": "Pilih templat...",
  "Applying a template pre-fills permissions below": "Menerapkan templat akan mengisi izin di bawah",
  "Access Scope": "Cakupan Akses",
  "All Branches": "Semua Cabang",
  "Specific Branch": "Cabang Tertentu",
  "Permissions": "Izin",
  "Select all": "Pilih semua",
  "Clear all": "Hapus semua",
  "Custom": "Khusus",
  "Default": "Bawaan",
  "permissions": "izin",
  "Permission History": "Riwayat Izin",
  "No permission changes yet": "Belum ada perubahan izin",
  "Delete this user?": "Hapus pengguna ini?",
  "Some permissions are restricted to Super Admin": "Sebagian izin hanya untuk Super Admin",
  "Selected": "Dipilih",
  "by": "oleh",
  // ---- Step 3: Cost Control ----
  "Avg Cost": "Biaya Rata2",
  "Value": "Nilai",
  "Cost Ledger": "Buku Besar Biaya",
  "Inventory cost transaction history — central warehouse": "Riwayat transaksi biaya inventaris — gudang pusat",
  "Cost In": "Biaya Masuk",
  "Cost Out": "Biaya Keluar",
  "Transactions": "Transaksi",
  "Unit Cost": "Biaya Satuan",
  "Total Cost": "Total Biaya",
  "Last Cost": "Biaya Terakhir",
  "No transactions in this range": "Tidak ada transaksi pada rentang ini",
  "Transfer Out": "Transfer Keluar",
  "Reports": "Laporan",
  "Purchase, supplier & inventory valuation reports": "Laporan pembelian, pemasok & valuasi inventaris",
  "Purchase Expense": "Biaya Pembelian",
  "Supplier Spending": "Belanja Pemasok",
  "Inventory Valuation": "Valuasi Inventaris",
  "Export CSV": "Ekspor CSV",
  "Total Expense": "Total Pengeluaran",
  "Received POs": "PO Diterima",
  "PO No": "No PO",
  "Received": "Diterima",
  "No data in this range": "Tidak ada data pada rentang ini",
  "Total Spend": "Total Belanja",
  "POs": "PO",
  "Spend": "Belanja",
  "Share": "Porsi",
  "Total Value": "Total Nilai",
  "SKUs": "SKU",
  "Invoice Intake": "Penerimaan Faktur",
  "Receive supplier invoices into the central warehouse": "Terima faktur pemasok ke gudang pusat",
  "New Invoice": "Faktur Baru",
  "Manual entry today. This is the entry point for future WhatsApp invoice upload & receipt OCR — confirming auto-updates inventory cost.": "Entri manual untuk saat ini. Ini titik masuk untuk unggah faktur WhatsApp & OCR struk di masa depan — konfirmasi otomatis memperbarui biaya inventaris.",
  "No invoices yet": "Belum ada faktur",
  "Confirm & Receive": "Konfirmasi & Terima",
  "Pending Review": "Menunggu Tinjauan",
  "Confirmed": "Dikonfirmasi",
  "Rejected": "Ditolak",
  "Add at least one item": "Tambahkan minimal satu item",
  "Invoice saved": "Faktur disimpan",
  "Received into warehouse": "Diterima ke gudang",
  "Save Invoice": "Simpan Faktur",
  "Select supplier...": "Pilih pemasok...",
  "Invoice Number": "Nomor Faktur",
  "Add Item": "Tambah Item",
  "Warehouse Inventory Value": "Nilai Inventaris Gudang",
  "Total Inventory Value": "Total Nilai Inventaris",
  "Purchases Today": "Pembelian Hari Ini",
  "Purchases This Month": "Pembelian Bulan Ini",
  "Sent To Branches": "Dikirim ke Cabang",
  // ---- Cost Insights (dashboard widgets) ----
  "Cost Insights": "Wawasan Biaya",
  "Inventory Value Trend": "Tren Nilai Inventaris",
  "Warehouse · last 30 days": "Gudang · 30 hari terakhir",
  "30-Day Purchase Spend": "Belanja Pembelian 30 Hari",
  "Goods received into warehouse": "Barang diterima ke gudang",
  "No purchases in the last 30 days": "Tidak ada pembelian dalam 30 hari terakhir",
  "Supplier Concentration": "Konsentrasi Pemasok",
  "Share of total purchase spend": "Porsi dari total belanja pembelian",
  "No supplier spend yet": "Belum ada belanja pemasok",
  "Top 5 by Inventory Value": "Top 5 Nilai Inventaris",
  "Highest-value stock on hand": "Stok bernilai tertinggi",
  "No inventory value yet": "Belum ada nilai inventaris",
  "Top Leakage Cost Products": "Produk Biaya Kebocoran Teratas",
  "Biggest Rp variance impact (loss + / surplus −)": "Dampak selisih Rp terbesar (rugi + / surplus −)",
  "loss": "rugi",
  "surplus": "surplus",
  "No leakage data yet — needs two finalized stock takes": "Belum ada data kebocoran — perlu dua stok opname final",
  // ---- P1: Variance Export + Weekly Leakage Summary ----
  "Weekly Leakage Summary": "Ringkasan Kebocoran Mingguan",
  "new this week": "baru minggu ini",
  "Highest Leakage Branch": "Cabang Kebocoran Tertinggi",
  "Export failed — needs two finalized stock takes": "Ekspor gagal — perlu dua stok opname final",
  "PDF": "PDF",
  "Excel": "Excel",
  // ---- Step 4A: Product Segmentation ----
  "Central Procurement": "Pengadaan Central",
  "Product Type": "Tipe Produk",
  "Raw Material": "Bahan Baku",
  "Internal Finished Good": "Barang Produksi",
  "Branch SKU": "Barang Cabang",
  "Visibility": "Visibilitas",
  "Central Inventory": "Inventory Central",
  "Recipe / BOM": "Resep / BOM",
  "Branch Ordering": "Pemesanan Cabang",
  "Product Classification": "Klasifikasi Produk",
  "Classification": "Klasifikasi",
  "Central Raw Material": "Bahan Baku Central",
  "Central Production Product": "Produk Produksi Central",
  "Branch Product": "Produk Cabang",
  "Classification & Visibility": "Klasifikasi & Visibilitas",
  "Select a classification to auto-fill visibility (you can still override).": "Pilih klasifikasi untuk mengisi visibilitas otomatis (masih bisa diubah).",
  "Example:": "Contoh:",
  "When production runs:": "Saat produksi dilakukan:",
  "Susu & Margarin stock decreases": "stok Susu & Margarin berkurang",
  "Saos Buttermilk stock increases": "stok Saos Buttermilk bertambah",
  "Branches can only see and order Saos Buttermilk.": "Cabang hanya dapat melihat dan memesan Saos Buttermilk.",
  "Show in Central Procurement": "Tampil di Pengadaan Central",
  "Show in Central Inventory": "Tampil di Inventory Central",
  "Show in Central Production": "Tampil di Produksi Central",
  "Show in Branch Ordering": "Tampil di Pesanan Cabang",
  "Unchecked products will not appear in that module.": "Produk yang tidak dicentang tidak akan muncul pada modul tersebut.",
  "Add Custom Item": "Tambah Item Custom",
  "Custom Item": "Item Custom",
  "Item Name": "Nama Item",
  "This order only has custom items (expense-only). Confirm to mark as received — no stock will be added.": "Pesanan ini hanya berisi item custom (hanya biaya). Konfirmasi untuk menandai diterima — stok tidak akan bertambah.",
  "Custom items in this order are expense-only and will not add stock.": "Item custom pada pesanan ini hanya biaya dan tidak menambah stok.",
  // ---- Step 4B: Central Production ----
  "Central Production": "Produksi Central",
  "Production Workflow": "Alur Produksi",
  "Production Engine": "Engine Produksi",
  "One production workspace — daily operational flow + the full production engine.": "Satu ruang kerja produksi — alur operasional harian + engine produksi lengkap.",
  "Central Kitchen production & transformation": "Produksi & transformasi Dapur Pusat",
  "Production": "Produksi",
  "Production Recipes": "Resep Produksi",
  "Production Recipe": "Resep Produksi",
  "New Recipe": "Resep Baru",
  "Edit Recipe": "Ubah Resep",
  "Delete this recipe?": "Hapus resep ini?",
  "Recipe deleted": "Resep dihapus",
  "Recipe created": "Resep dibuat",
  "Recipe updated": "Resep diperbarui",
  "No production recipes yet": "Belum ada resep produksi",
  "Recipe Name": "Nama Resep",
  "Output Product": "Produk Output",
  "Output": "Output",
  "Input Materials": "Bahan Input",
  "Add Input": "Tambah Bahan",
  "Select material...": "Pilih bahan...",
  "Fill name, output and at least one input": "Isi nama, output, dan minimal satu bahan",
  "New Production": "Produksi Baru",
  "No production batches yet": "Belum ada batch produksi",
  "Production No": "No Produksi",
  "Recipe": "Resep",
  "Planned": "Rencana",
  "Actual": "Aktual",
  "Yield": "Hasil",
  "Created By": "Dibuat Oleh",
  "Detail": "Detail",
  "Confirm": "Konfirmasi",
  "Draft": "Draft",
  "Confirmed": "Dikonfirmasi",
  "Cancelled": "Dibatalkan",
  "Batch Multiplier": "Pengali Batch",
  "Planned Output": "Output Rencana",
  "Will consume": "Akan mengonsumsi",
  "Create Draft": "Buat Draft",
  "Create a production recipe first": "Buat resep produksi terlebih dahulu",
  "Select a recipe and valid multiplier": "Pilih resep dan pengali yang valid",
  "Production draft created": "Draft produksi dibuat",
  "Confirm Production": "Konfirmasi Produksi",
  "Actual Output Quantity": "Jumlah Output Aktual",
  "Enter a valid actual output": "Masukkan output aktual yang valid",
  "Production confirmed": "Produksi dikonfirmasi",
  "Cancel Batch": "Batalkan Batch",
  "Cancel this draft batch?": "Batalkan batch draft ini?",
  "Batch cancelled": "Batch dibatalkan",
  "Stok tidak mencukupi untuk produksi": "Stok tidak mencukupi untuk produksi",
  "Material": "Bahan",
  "Current Stock": "Stok Saat Ini",
  "Required": "Kebutuhan",
  "Shortage": "Kekurangan",
  "Production Detail": "Detail Produksi",
  "Consumed Materials": "Bahan Dikonsumsi",
  "Cost Rollup Summary": "Ringkasan Biaya",
  "Total Input Cost": "Total Biaya Input",
  "Output Unit Cost": "Biaya Satuan Output",
  "Stock Ledger References": "Referensi Buku Stok",
  "Production Today": "Produksi Hari Ini",
  "Production This Month": "Produksi Bulan Ini",
  "Average Yield": "Rata-rata Hasil",
  "Production Variance": "Selisih Produksi",
  "Top Produced Items": "Produk Terbanyak Diproduksi",
  "No confirmed production yet": "Belum ada produksi terkonfirmasi",
  // ---- Production Insights (dashboard) ----
  "Production Insights": "Wawasan Produksi",
  "Production Value Today": "Nilai Produksi Hari Ini",
  "Production Value This Month": "Nilai Produksi Bulan Ini",
  "Production Efficiency": "Efisiensi Produksi",
  "Production Loss Value": "Nilai Kerugian Produksi",
  "Production Yield Trend": "Tren Hasil Produksi",
  "Average yield % · last 30 days": "Rata-rata hasil % · 30 hari terakhir",
  "Planned vs actual output · this month": "Rencana vs aktual · bulan ini",
  "efficiency": "efisiensi",
  "Highest output this month": "Output tertinggi bulan ini",
  // ---- Step 4C: Production Costing & HPP ----
  "Cost Settings": "Pengaturan Biaya",
  "Production Cost Settings": "Pengaturan Biaya Produksi",
  "Manufacturing Overhead (% of material cost)": "Overhead Produksi (% dari biaya material)",
  "Overhead is added to HPP only and does not affect inventory valuation.": "Overhead hanya ditambahkan ke HPP dan tidak memengaruhi valuasi inventaris.",
  "Cost settings updated": "Pengaturan biaya diperbarui",
  "Standard Labor Cost": "Biaya Tenaga Kerja Standar",
  "Labor Cost": "Biaya Tenaga Kerja",
  "Material Cost": "Biaya Material",
  "Overhead Cost": "Biaya Overhead",
  "Overhead": "Overhead",
  "of material cost": "dari biaya material",
  "HPP = Material + Labor + Overhead (reporting only — does not change inventory value)": "HPP = Material + Tenaga Kerja + Overhead (hanya laporan — tidak mengubah nilai inventaris)",
  "HPP Breakdown": "Rincian HPP",
  "Total HPP": "Total HPP",
  "HPP per Unit": "HPP per Unit",
  "Transfer Price": "Harga Transfer",
  "Gross Margin": "Marjin Kotor",
  "Margin": "Marjin",
  "HPP is a reporting figure. Inventory is valued at material cost only.": "HPP adalah angka laporan. Inventaris dinilai hanya pada biaya material.",
  "Transfer / Selling Price (Rp)": "Harga Transfer / Jual (Rp)",
  "HPP Composition": "Komposisi HPP",
  "Material / Labor / Overhead · this month": "Material / Tenaga Kerja / Overhead · bulan ini",
  "Avg HPP per Unit": "Rata-rata HPP per Unit",
  "Avg Gross Margin": "Rata-rata Marjin Kotor",
  "Default from recipe · override here if needed": "Bawaan dari resep · timpa di sini bila perlu",
  // ---- Navigation groups & items (operational grouping) ----
  "Central Warehouse": "Gudang Central",
  "Operations": "Operasional",
  "Analytics": "Analitik",
  "Administration": "Administrasi",
  "Purchase Order": "Pesanan Pembelian",
  "Production Dashboard": "Dashboard Produksi",
  "Production Batches": "Batch Produksi",
  "Production History": "Riwayat Produksi",
  "Production HPP": "HPP Produksi",
  // ---- Step 4D: Branch Transfer Pricing & Margin Dashboard ----
  "Margin Dashboard": "Dashboard Marjin",
  "Branch Transfer Pricing & Margin": "Harga Transfer Cabang & Marjin",
  "Branch Selling Price": "Harga Jual Cabang",
  "Central Margin": "Marjin Central",
  "Branch Margin": "Marjin Cabang",
  "Total Margin": "Total Marjin",
  "Reference HPP": "HPP Referensi",
  "Reference HPP (rolling 30-day average)": "HPP Referensi (rata-rata 30 hari)",
  "Recommended Transfer Price": "Harga Transfer Disarankan",
  "Recommended": "Disarankan",
  "Use Recommended": "Gunakan Disarankan",
  "Margin Ranking": "Peringkat Marjin",
  "Highest-margin products": "Produk dengan marjin tertinggi",
  "Margin Losers": "Marjin Terendah",
  "Lowest-margin products": "Produk dengan marjin terendah",
  "Top Margin Contributors": "Kontributor Marjin Teratas",
  "Margin contribution = margin × 30-day volume": "Kontribusi marjin = marjin × volume 30 hari",
  "Margin Trend": "Tren Marjin",
  "Average central margin % · last 30 days": "Rata-rata marjin central % · 30 hari terakhir",
  "Avg Central Margin": "Rata-rata Marjin Central",
  "Avg Branch Margin": "Rata-rata Marjin Cabang",
  "Items Below Threshold": "Item Di Bawah Ambang",
  "Selling Below HPP": "Jual Di Bawah HPP",
  "Healthy": "Sehat",
  "Medium": "Sedang",
  "Low Margin": "Marjin Rendah",
  "Loss": "Rugi",
  "Not Priced": "Belum Ada Harga",
  "No finished goods to analyze yet": "Belum ada barang jadi untuk dianalisis",
  "Set finished-good (transfer/selling) prices to see margins here.": "Atur harga barang jadi (transfer/jual) untuk melihat marjin di sini.",
  "Set Price": "Atur Harga",
  "Branch Price Override": "Override Harga Cabang",
  "Global (all branches)": "Global (semua cabang)",
  "Pricing Settings": "Pengaturan Marjin",
  "Margin Thresholds": "Ambang Marjin",
  "Low Margin Threshold (%)": "Ambang Marjin Rendah (%)",
  "Healthy Margin Threshold (%)": "Ambang Marjin Sehat (%)",
  "Target Margin for Recommended Price (%)": "Target Marjin untuk Harga Disarankan (%)",
  "Pricing settings updated": "Pengaturan marjin diperbarui",
  "Branch price updated": "Harga cabang diperbarui",
  "Product Margin": "Marjin Produk",
  "Transfer Price Analysis": "Analisis Harga Transfer",
  "Export CSV": "Ekspor CSV",
  "Margins compare HPP → Transfer Price → Branch Selling Price. HPP, inventory valuation and all workflows are unchanged.": "Marjin membandingkan HPP → Harga Transfer → Harga Jual Cabang. HPP, valuasi inventaris, dan semua alur kerja tidak berubah.",
  "Status": "Status",
  "Product": "Produk",
  "Save Price": "Simpan Harga",
  "Leave blank to use the global price.": "Kosongkan untuk memakai harga global.",
  "Manage thresholds & target margin": "Atur ambang & target marjin",
  "vol": "vol",
  "contribution": "kontribusi",

  // ---- Master Data ----
  "Products, categories, branches, suppliers & units": "Produk, kategori, cabang, pemasok & unit",
  "Products": "Produk",
  "Categories": "Kategori",
  "Branches": "Cabang",
  "Units": "Unit",
  "Add Product": "Tambah Produk",
  // ---- Expiry (HQ) ----
  "Expiry Management": "Manajemen Kadaluarsa",
  "Batch & expiry tracking — expired, ≤7 days, ≤30 days": "Pelacakan batch & kadaluarsa — kadaluarsa, ≤7 hari, ≤30 hari",
  "Add Batch": "Tambah Batch",
  "Expiring ≤7d": "Kad ≤7h",
  "Expiring ≤30d": "Kad ≤30h",
  "No date": "Tanpa tanggal",
  "Expiring ≤7 Days": "Kadaluarsa ≤7 Hari",
  "Expiring ≤30 Days": "Kadaluarsa ≤30 Hari",
  "Product and expiry date are required": "Produk dan tanggal kadaluarsa wajib diisi",
  "Batch added": "Batch ditambahkan",
  "Removed": "Dihapus",
  "Remove this batch record?": "Hapus catatan batch ini?",
  "No batches in this view. Batch & expiry are captured on stock-in / PO receive, or add manually.": "Tidak ada batch di tampilan ini. Batch & kadaluarsa dicatat saat stok masuk / penerimaan PO, atau tambah manual.",
  "Location": "Lokasi",
  "Batch": "Batch",
  "Expiry": "Kadaluarsa",
  "Status": "Status",
  "Batch No.": "No. Batch",
  "Expiry Date": "Tanggal Kadaluarsa",
  "Product": "Produk",
  "Quantity": "Jumlah",

  // ================= BATCH 2 COMPLETION — wrapped page strings =================
  // ---- Owner Dashboard ----
  "Welcome": "Selamat datang",
  "Stock-take interval updated": "Interval stok opname diperbarui",
  "Created": "Dibuat",
  "draft purchase order(s)": "draf pesanan pembelian",
  "Nothing to reorder": "Tidak ada yang perlu dipesan ulang",
  "Today's Purchases": "Pembelian Hari Ini",
  "Monthly Waste": "Barang Rusak Bulanan",
  "Critical Items": "Item Kritis",
  "Low Items": "Item Menipis",
  "Pending Opname": "Stok Opname Menunggu",
  "Central Warehouse Health": "Kesehatan Gudang Pusat",
  "tracked products in central warehouse": "produk terlacak di gudang pusat",
  "Variance": "Selisih",
  "No stock take": "Tidak ada stok opname",
  "Top 10 Lowest Stock Items": "10 Item Stok Terendah",
  "Central + all branches · most urgent first": "Pusat + semua cabang · paling mendesak dulu",
  "Current / Min": "Saat Ini / Min",
  "Items at or below minimum · suggested top-up to ideal": "Item di/di bawah minimum · saran tambah ke ideal",
  "Creating...": "Membuat...",
  "Create Purchase Draft": "Buat Draf Pembelian",
  "No reorders needed — all stock above minimum.": "Tidak perlu pemesanan ulang — semua stok di atas minimum.",
  "Current / Ideal": "Saat Ini / Ideal",
  "Suggested Order": "Saran Pesanan",
  "Stock Health by Branch": "Kesehatan Stok per Cabang",
  "Purchasing Trend (7 days)": "Tren Pembelian (7 hari)",
  "Waste Trend (7 days)": "Tren Barang Rusak (7 hari)",
  "Branch Submission Status (Today)": "Status Pengiriman Cabang (Hari Ini)",
  "Order Submitted": "Pesanan Dikirim",
  "No Order": "Tidak Ada Pesanan",
  "Closed": "Ditutup",
  "Branch Performance (Orders this month)": "Kinerja Cabang (Pesanan bulan ini)",

  // ---- Inventory (central + branch) ----
  "Stock added": "Stok ditambahkan",
  "OCR detected": "OCR mendeteksi",
  "OCR failed": "OCR gagal",
  "Parsed": "Terbaca",
  "Parse failed": "Gagal membaca",
  "Match at least one product": "Cocokkan minimal satu produk",
  "items added to stock": "item ditambahkan ke stok",
  "Levels updated": "Level diperbarui",
  "Enter new quantity and reason": "Masukkan jumlah baru dan alasan",
  "Large adjustment submitted for Owner approval": "Penyesuaian besar dikirim untuk persetujuan Pemilik",
  "Branch stock adjusted": "Stok cabang disesuaikan",
  "Track central warehouse stock levels": "Lacak level stok gudang pusat",
  "Track branch-level stock balances": "Lacak saldo stok tingkat cabang",
  "Receipt OCR": "OCR Struk",
  "Branch Inventory": "Inventaris Cabang",
  "Search inventory...": "Cari inventaris...",
  "All Branches": "Semua Cabang",
  "Current": "Saat Ini",
  "Stock Health": "Kesehatan Stok",
  "No branch inventory records found.": "Tidak ada catatan inventaris cabang.",
  "Actions": "Aksi",
  "Levels": "Level",
  "Manual Stock In": "Stok Masuk Manual",
  "Add Stock": "Tambah Stok",
  "Note": "Catatan",
  "WhatsApp Text Import": "Impor Teks WhatsApp",
  "Parse": "Baca",
  "Paste a WhatsApp stock message. Format:": "Tempel pesan stok WhatsApp. Format:",
  "per line.": "per baris.",
  "Confirm Stock Update": "Konfirmasi Pembaruan Stok",
  "Confirm & Add Stock": "Konfirmasi & Tambah Stok",
  "Review detected items and match to products before updating stock.": "Tinjau item terdeteksi dan cocokkan dengan produk sebelum memperbarui stok.",
  "Detected:": "Terdeteksi:",
  "no match": "tidak cocok",
  "Save Levels": "Simpan Level",
  "Set Levels": "Atur Level",
  "Set minimum & ideal stock for this product at the branch. Status recalculates automatically.": "Atur stok minimum & ideal produk ini di cabang. Status dihitung ulang otomatis.",
  "Minimum Stock": "Stok Minimum",
  "Ideal Stock": "Stok Ideal",
  "Current Stock (optional override)": "Stok Saat Ini (override opsional)",
  "Leave blank to keep current": "Biarkan kosong untuk mempertahankan stok",
  "Save Adjustment": "Simpan Penyesuaian",
  "Set the new actual on-hand quantity at this branch. A movement is logged to the ledger.": "Atur jumlah aktual stok di cabang ini. Pergerakan dicatat ke riwayat.",
  "New Current Quantity": "Jumlah Saat Ini Baru",
  "Select reason...": "Pilih alasan...",
  "This is a large adjustment": "Ini penyesuaian besar",
  "It will be submitted for Owner / Super Admin approval before taking effect.": "Akan dikirim untuk persetujuan Pemilik / Super Admin sebelum berlaku.",
  "Physical Count": "Hitung Fisik",
  "Damaged Stock": "Stok Rusak",
  "Wrong Receiving": "Salah Penerimaan",
  "Wrong Entry": "Salah Input",
  "Expired Product": "Produk Kadaluarsa",
  "Other": "Lainnya",

  // ---- Procurement ----
  "Add at least one item": "Tambahkan minimal satu item",
  "Draft purchase order created": "Draf pesanan pembelian dibuat",
  "Goods received & stocked in": "Barang diterima & masuk stok",
  "Cancel this purchase order?": "Batalkan pesanan pembelian ini?",
  "Total": "Total",
  "Unit Price": "Harga Satuan",
  "Line Total": "Total Baris",
  "Confirm Receipt": "Konfirmasi Penerimaan",
  "Confirm received quantities. Optionally record batch number & expiry date (recommended for perishables).": "Konfirmasi jumlah diterima. Opsional catat nomor batch & tanggal kadaluarsa (disarankan untuk barang mudah rusak).",
  "Purchase orders & cost analytics": "Pesanan pembelian & analitik biaya",
  "New Purchase Order": "Pesanan Pembelian Baru",
  "No purchase orders yet.": "Belum ada pesanan pembelian.",
  "PO No.": "No. PO",
  "Items": "Item",
  "Total Spend (received)": "Total Belanja (diterima)",
  "Received POs": "PO Diterima",
  "Monthly Spend Trend": "Tren Belanja Bulanan",
  "No received purchase data yet.": "Belum ada data pembelian diterima.",
  "Top Spend Products": "Produk Belanja Terbesar",
  "No data": "Tidak ada data",
  "Supplier Spend": "Belanja per Pemasok",
  "Create Draft": "Buat Draf",
  "Unassigned": "Belum ditentukan",
  "Add Item": "Tambah Item",
  "Price": "Harga",
  "CANCELLED": "Dibatalkan",
  "DELIVERY_ADJUSTMENT": "Penyesuaian Pengiriman",
  "PO submitted": "PO dikirim",
  "PO approved": "PO disetujui",
  "PO cancelled": "PO dibatalkan",
  "reorder": "reorder",

  // ---- Notifications ----
  "Notification Center": "Pusat Notifikasi",
  "WhatsApp notification log & delivery queue": "Log notifikasi WhatsApp & antrian pengiriman",
  "All marked read": "Semua ditandai dibaca",
  "Failed to mark read": "Gagal menandai dibaca",
  "Flushed": "Terkirim",
  "notifications": "notifikasi",
  "You do not have permission to send notifications": "Anda tidak memiliki izin untuk mengirim notifikasi",
  "WhatsApp Cloud API": "WhatsApp Cloud API",
  "Connected": "Terhubung",
  "Not configured": "Belum dikonfigurasi",
  "Notifications are delivered live via WhatsApp.": "Notifikasi dikirim langsung via WhatsApp.",
  "Notifications are stored & queued. Add Meta credentials to activate live sending — no workflow changes needed.": "Notifikasi disimpan & diantrikan. Tambahkan kredensial Meta untuk mengaktifkan pengiriman langsung — tanpa perubahan alur kerja.",
  "Live": "Langsung",
  "Queued": "Antrian",
  "No notifications yet": "Belum ada notifikasi",
  "System events like order submissions and low-stock alerts will appear here.": "Peristiwa sistem seperti pengiriman pesanan dan peringatan stok menipis akan tampil di sini.",
  "sent": "terkirim",
  "queued": "antrian",
  "failed": "gagal",

  // ---- Audit ----
  "Every action, fully tracked": "Setiap aksi, terlacak penuh",
  "No activity yet": "Belum ada aktivitas",
  "Actions across the platform will be logged here.": "Aksi di seluruh platform akan dicatat di sini.",

  // ---- Users ----
  "Fill all required fields": "Isi semua kolom wajib",
  "User created": "Pengguna dibuat",
  "Manage platform access": "Kelola akses platform",
  "Last Login": "Login Terakhir",
  "New User": "Pengguna Baru",
  "Create": "Buat",

  // ---- Master Data ----
  "Product saved": "Produk disimpan",
  "Product deleted": "Produk dihapus",
  "Saved": "Disimpan",
  "Branch updated": "Cabang diperbarui",
  "Products, categories, units & suppliers": "Produk, kategori, unit & pemasok",
  "Unit": "Unit",
  "Min/Ideal": "Min/Ideal",
  "Yes": "Ya",
  "Edit Product": "Ubah Produk",
  "New Product": "Produk Baru",
  "New": "Baru",
  "Product Image": "Gambar Produk",
  "Inventory Type": "Tipe Inventaris",
  "Stocked": "Stok",
  "Default Supplier": "Pemasok Default",
  "Critical Alert (% of min)": "Peringatan Kritis (% dari min)",
  "Default Price (Rp)": "Harga Default (Rp)",
  "Suppliers & Prices": "Pemasok & Harga",
  "+ Add supplier": "+ Tambah pemasok",
  "Select supplier...": "Pilih pemasok...",
  "Pref": "Utama",
  "No additional suppliers. The default supplier & price above will be used.": "Tidak ada pemasok tambahan. Pemasok & harga default di atas akan digunakan.",
  "Track Inventory": "Lacak Inventaris",
  "STOCKED": "STOK",
  "PASSTHROUGH": "PASSTHROUGH",
  "Color": "Warna",
  "Contact": "Kontak",
  "Phone": "Telepon",
  "Address": "Alamat",
  "Branch Logo": "Logo Cabang",
  "Branch Name": "Nama Cabang",
  "Logo": "Logo",
  "Permanently delete": "Hapus permanen",
  "This cannot be undone.": "Tindakan ini tidak dapat dibatalkan.",
  "Remove": "Hapus",

  // ---- Company Profile ----
  "Company Profile": "Profil Perusahaan",
  "Company Name": "Nama Perusahaan",
  "Warehouse Name": "Nama Gudang",
  "Tax Number (NPWP)": "NPWP",
  "Company Logo": "Logo Perusahaan",
  "App Logo": "Logo Aplikasi",
  "Document Logo": "Logo Dokumen",
  "Shown in the sidebar, header & login screen.": "Tampil di sidebar, header & layar masuk.",
  "Shown on Surat Jalan & Faktur PDFs.": "Tampil di PDF Surat Jalan & Faktur.",
  "Save Profile": "Simpan Profil",
  "Company profile updated": "Profil perusahaan diperbarui",
  "These details appear on all documents (Surat Jalan, Faktur) and reports.": "Detail ini muncul di semua dokumen (Surat Jalan, Faktur) dan laporan.",
  "New Order · number assigned on save": "Pesanan Baru · nomor diberikan saat disimpan",
  "Restaurant Ops": "Operasi Restoran",

  // ---- Branch Dashboard (POS redesign) ----
  "Hi, Welcome": "Halo, Selamat Datang",
  "Your branch today": "Cabang Anda hari ini",
  "Health Score": "Skor Kesehatan",
  "Today's Tasks": "Tugas Hari Ini",
  "Quick Actions": "Aksi Cepat",
  "Create Order": "Buat Pesanan",
  "Receive Goods": "Terima Barang",
  "Start Stock Take": "Mulai Stok Opname",
  "Record Waste": "Catat Barang Rusak",
  "Input Sales": "Input Penjualan",
  "Goods Arriving Today": "Barang Datang Hari Ini",
  "Stock Take Today": "Stok Opname Hari Ini",
  "Damaged Goods Today": "Barang Rusak Hari Ini",
  "Running Low — Order Now": "Stok Menipis — Pesan Sekarang",
  "Add to Order": "Tambahkan ke Pesanan",
  "Recommended": "Rekomendasi",
  "Added to order": "Ditambahkan ke pesanan",
  "No items need ordering right now.": "Tidak ada barang yang perlu dipesan saat ini.",
  "Nothing to receive right now.": "Tidak ada barang untuk diterima saat ini.",
  // ---- Receiving (verify redesign) ----
  "Verify Goods": "Periksa Barang",
  "Tap each item to confirm it matches the delivery.": "Ketuk tiap barang untuk memastikan sesuai pengiriman.",
  "Matches": "Sesuai",
  "Has Difference": "Ada Selisih",
  "Actual Received": "Jumlah Diterima",
  "All items match — ready to receive.": "Semua barang sesuai — siap diterima.",
  "item(s) with a difference": "barang dengan selisih",
  "Inspect & Receive": "Periksa & Terima",
  "Check & Receive": "Periksa & Terima",
  "Deliveries from the central warehouse will appear here.": "Pengiriman dari gudang pusat akan tampil di sini.",

  // ---- Owner Dashboard (cute redesign + branch selector) ----
  "Add Branch": "Tambah Cabang",
  "Here's today's operational summary across all branches.": "Berikut ringkasan operasional seluruh cabang hari ini.",
  "Here's today's summary for": "Berikut ringkasan hari ini untuk",
  "vs yesterday": "dari kemarin",
  "Today": "Hari Ini",
  "This Month": "Bulan Ini",
  "Orders This Month": "Pesanan Bulan Ini",
  "Top Supplier": "Supplier Terbaik",
  "Need Help?": "Butuh Bantuan?",
  "Contact the Central Flow team!": "Hubungi tim Central Flow!",
  "Chat via WhatsApp": "Chat via WhatsApp",
  "Active Today": "Aktif Hari Ini",
  "branches ordered today": "cabang sudah pesan hari ini",
  "No address": "Tanpa alamat",
  "Branch Overview": "Ringkasan Cabang",
  "Stock Value": "Nilai Stok",
  "Waste %": "Barang Rusak %",
  "Submitted today": "Sudah pesan hari ini",
  "Not yet ordered today": "Belum pesan hari ini",
  "Last count": "Hitung terakhir",
  "by spend": "berdasarkan belanja",
  "of branches": "dari cabang",
  "Everything looks great!": "Semua terlihat baik!",
  "items": "item",
  "to order": "untuk dipesan",
  "Order": "Pesan",

  // ---- Recipes ----
  "Delete/archive": "Hapus/arsipkan",
  "Yield %": "Yield %",
  "Prep loss %": "Susut olah %",
  "ingredients": "bahan",
  "from": "dari",
  "e.g. Main, Drink": "mis. Utama, Minuman",

  // ---- Ledger ----
  "Purchase": "Pembelian",
  "Stock In": "Stok Masuk",
  "Return In": "Retur Masuk",
  "Adjustment +": "Penyesuaian +",
  "Branch Receive": "Terima Cabang",
  "Delivery Out": "Kirim Keluar",
  "Stock Out": "Stok Keluar",
  "Adjustment −": "Penyesuaian −",
  "Adjustment": "Penyesuaian",
  "Return to Supplier": "Retur ke Pemasok",
  "Return": "Retur",
  "Stock Movement Ledger": "Riwayat Pergerakan Stok",
  "Single source of truth — every stock change, fully traceable": "Sumber kebenaran tunggal — setiap perubahan stok, terlacak penuh",
  "All Locations": "Semua Lokasi",
  "From": "Dari",
  "To": "Ke",
  "When": "Waktu",
  "Before → After": "Sebelum → Sesudah",
  "No movements in this range": "Tidak ada pergerakan pada rentang ini",

  // ---- Purchasing ----
  "approved": "disetujui",
  "marked purchased": "ditandai dibeli",
  "Checklist saved": "Checklist disimpan",
  "Aggregated demand & order approvals": "Permintaan teragregasi & persetujuan pesanan",
  "Purchasing Summary": "Ringkasan Pembelian",
  "Orders Pipeline": "Alur Pesanan",
  "Aggregated Demand": "Permintaan Teragregasi",
  "submitted/approved orders": "pesanan dikirim/disetujui",
  "No pending demand. All caught up!": "Tidak ada permintaan tertunda. Semua beres!",
  "No orders awaiting action.": "Tidak ada pesanan menunggu tindakan.",
  "Checklist": "Daftar Periksa",
  "Mark Purchased": "Tandai Dibeli",
  "Available": "Tersedia",
  "Partial": "Sebagian",
  "Unavailable": "Tidak Tersedia",
  "AVAILABLE": "Tersedia",
  "PARTIAL": "Sebagian",
  "UNAVAILABLE": "Tidak Tersedia",

  // ---- Delivery ----
  "Delivery confirmed & stock updated": "Pengiriman dikonfirmasi & stok diperbarui",
  "Delivery & Documents": "Pengiriman & Dokumen",
  "Adjust deliveries and generate Surat Jalan / Invoice": "Sesuaikan pengiriman dan buat Surat Jalan / Faktur",
  "No orders ready for delivery.": "Tidak ada pesanan siap dikirim.",
  "Adjust & Deliver": "Sesuaikan & Kirim",
  "Surat Jalan": "Surat Jalan",
  "Delivery Adjustment": "Penyesuaian Pengiriman",
  "Confirm Delivery": "Konfirmasi Pengiriman",
  "Original ordered quantities are preserved. Set the actual delivered quantity.": "Jumlah pesanan asli dipertahankan. Atur jumlah yang benar-benar dikirim.",
  "added": "tambahan",
  "Add additional item": "Tambah item tambahan",

  // ---- Daily Closing ----
  "Daily closing saved": "Tutup harian disimpan",
  "Daily Closing Checklist": "Daftar Tutup Harian",
  "Confirm end-of-day tasks": "Konfirmasi tugas akhir hari",
  "Waste Submitted": "Barang Rusak Dilaporkan",
  "Branch order for today has been submitted": "Pesanan cabang hari ini telah dikirim",
  "Today's waste has been reported": "Barang rusak hari ini telah dilaporkan",
  "Daily Closing Completed": "Tutup Harian Selesai",
  "All end-of-day tasks finalized": "Semua tugas akhir hari diselesaikan",
  "Save Checklist": "Simpan Daftar",

  // ---- Feedback ----
  "Bug": "Bug",
  "Improvement": "Perbaikan",
  "Missing Product": "Produk Hilang",
  "Workflow Issue": "Masalah Alur Kerja",
  "Status updated": "Status diperbarui",
  "Feedback Review": "Tinjauan Masukan",
  "open": "terbuka",
  "total": "total",
  "all": "semua",
  "resolved": "selesai",
  "No feedback yet": "Belum ada masukan",
  "Branch feedback submitted during the pilot will appear here.": "Masukan cabang yang dikirim selama uji coba akan tampil di sini.",
  "Mark Resolved": "Tandai Selesai",
  "Screenshot": "Tangkapan Layar",

  // ---- Approvals (extra) ----
  "No adjustment requests.": "Tidak ada permintaan penyesuaian.",
  "> Rp 100k": "> Rp 100rb",
  "> 10% stock": "> 10% stok",

  // ---- Feedback widget ----
  "Send Feedback": "Kirim Masukan",
  "Page": "Halaman",
  "Feedback Type": "Jenis Masukan",
  "Describe the issue or idea...": "Jelaskan masalah atau ide...",
  "Screenshot (optional)": "Tangkapan Layar (opsional)",
  "Change image": "Ganti gambar",
  "Attach a screenshot": "Lampirkan tangkapan layar",
  "Screenshot too large (max 3MB)": "Tangkapan layar terlalu besar (maks 3MB)",
  "Please add some notes": "Mohon tambahkan catatan",
  "Thanks! Your feedback was sent.": "Terima kasih! Masukan Anda terkirim.",
  "Failed to send feedback": "Gagal mengirim masukan",
  // --- Phase AI-1: AI Assistant ---
  "AI Assistant": "Asisten AI",
  "Read-only operations & inventory intelligence": "Intelijen operasi & inventaris (hanya baca)",
  "This assistant only reads and analyzes data. It never makes changes, transfers, purchases, or production. Recommendations only.": "Asisten ini hanya membaca & menganalisis data. Tidak pernah melakukan perubahan, transfer, pembelian, atau produksi. Hanya memberi rekomendasi.",
  "Ask anything about your operations": "Tanyakan apa saja tentang operasi Anda",
  "Inventory, production, sales, transfers, procurement and variance — answered from your real data.": "Inventaris, produksi, penjualan, transfer, pengadaan, dan variance — dijawab dari data Anda yang sebenarnya.",
  "AI is analyzing your data…": "AI sedang menganalisis data Anda…",
  "Type your question… e.g. Produk apa yang hampir habis?": "Ketik pertanyaan Anda… mis. Produk apa yang hampir habis?",
  "Send": "Kirim",
  "Something went wrong": "Terjadi kesalahan",
  "No items": "Tidak ada item",
  "Failed to load": "Gagal memuat",
  "Based on": "Berdasarkan",
  "tracked products": "produk dipantau",
  "usage window": "jendela penggunaan",
  "d": "h",
  "Refresh": "Segarkan",
  "Near Out of Stock": "Hampir Habis",
  "At/below minimum or < 7 days cover": "Pada/di bawah minimum atau < 7 hari stok",
  "Overstock": "Kelebihan Stok",
  "Current well above ideal": "Saat ini jauh di atas ideal",
  "Dead Stock": "Stok Mati",
  "No movement in 60 days": "Tidak ada pergerakan dalam 60 hari",
  "Slow Moving": "Pergerakan Lambat",
  "> 45 days of cover": "> 45 hari stok",
  "Highest Inventory Value": "Nilai Inventaris Tertinggi",
  "By Rupiah value": "Berdasarkan nilai Rupiah",
  "Inventory Coverage": "Cakupan Inventaris (hari)",
  "Lowest days of cover first": "Hari stok terendah lebih dulu",
  "Days": "Hari",
  "Usage/d": "Pakai/hari",
  "The request took too long. Please try again.": "Permintaan terlalu lama. Silakan coba lagi.",
  "Cannot reach the server. Please check your connection and try again.": "Tidak dapat terhubung ke server. Periksa koneksi Anda dan coba lagi.",
  // --- Phase AI-3A: Raw Material Price Changes ---
  "Price Changes": "Perubahan Harga",
  "Raw Material Price Changes": "Perubahan Harga Bahan Baku",
  "AI-assisted, approval-controlled Product Master price updates": "Perubahan harga Master Produk berbantuan AI, terkontrol persetujuan",
  "AI prepares price-change drafts only. Prices update ONLY after an Owner or Super Admin approves. Selling prices, transfer prices and historical HPP are never auto-changed.": "AI hanya menyiapkan draf perubahan harga. Harga berubah HANYA setelah disetujui Owner atau Super Admin. Harga jual, harga transfer, dan HPP historis tidak pernah diubah otomatis.",
  "AI / Text": "AI / Teks",
  "Manual": "Manual",
  "e.g. Harga cabe merah naik 20 ribu per kg": "mis. Harga cabe merah naik 20 ribu per kg",
  "Analyze": "Analisa",
  "Multiple products match. Choose one:": "Beberapa produk cocok. Pilih salah satu:",
  "Raw Material": "Bahan Baku",
  "Select product": "Pilih produk",
  "Select a product and enter a new price": "Pilih produk dan masukkan harga baru",
  "New Price": "Harga Baru",
  "Current Price": "Harga Saat Ini",
  "Preview": "Pratinjau",
  "Cancel": "Batal",
  "Create Draft (Pending Approval)": "Buat Draf (Menunggu Persetujuan)",
  "Price change request created (pending approval)": "Permintaan perubahan harga dibuat (menunggu persetujuan)",
  "Product": "Produk",
  "Difference": "Selisih",
  "CRITICAL": "Kritis",
  "HIGH": "Tinggi",
  "NORMAL": "Normal",
  "Critical price change (over 50%). Review carefully before approving.": "Perubahan harga kritis (di atas 50%). Tinjau dengan saksama sebelum menyetujui.",
  "High impact price change (over 20%). Please review the affected products.": "Perubahan harga berdampak tinggi (di atas 20%). Mohon tinjau produk yang terpengaruh.",
  "Affected Products": "Produk Terpengaruh",
  "Affected Recipes": "Resep Terpengaruh",
  "Est. HPP Impact": "Estimasi Dampak HPP",
  "Below Target After": "Di Bawah Target Setelahnya",
  "Cost": "Biaya",
  "Menu": "Menu",
  "Production": "Produksi",
  "No products use this material in any recipe.": "Tidak ada produk yang memakai bahan ini dalam resep.",
  "No price change requests": "Belum ada permintaan perubahan harga",
  "Create one above using AI text or the manual form.": "Buat di atas menggunakan teks AI atau formulir manual.",
  "Reference": "Referensi",
  "Old": "Lama",
  "New": "Baru",
  "Change": "Perubahan",
  "Requested by": "Diminta oleh",
  "Approved by": "Disetujui oleh",
  "Decided by": "Diputuskan oleh",
  "Source": "Sumber",
  "All": "Semua",
  "Pending": "Menunggu",
  "Approved": "Disetujui",
  "Rejected": "Ditolak",
  "Menunggu Persetujuan": "Menunggu Persetujuan",
  "Disetujui": "Disetujui",
  "Ditolak": "Ditolak",
  "Price Change": "Perubahan Harga",
  "Reject": "Tolak",
  "Approve": "Setujui",
  "Close": "Tutup",
  "Approved — Product Master price updated": "Disetujui — harga Master Produk diperbarui",
  "Request rejected": "Permintaan ditolak",
  // --- Procurement Foundation 1.0 ---
  "Central Purchase": "Pembelian Central",
  "Branch Purchase": "Pembelian Cabang",
  "Record raw-material purchases — inventory & price updates": "Catat pembelian bahan baku — pembaruan stok & harga",
  "Branch-level purchases & operational expenses": "Pembelian tingkat cabang & pengeluaran operasional",
  "Purchases": "Pembelian",
  "Dashboard & Suppliers": "Dasbor & Supplier",
  "New Purchase": "Pembelian Baru",
  "New Central Purchase": "Pembelian Central Baru",
  "New Branch Purchase": "Pembelian Cabang Baru",
  "Confirm Purchase": "Konfirmasi Pembelian",
  "Supplier": "Supplier",
  "— Optional —": "— Opsional —",
  "Invoice Number": "Nomor Faktur",
  "Invoice Date": "Tanggal Faktur",
  "Purchase Date": "Tanggal Pembelian",
  "Qty": "Jml",
  "Unit Price": "Harga Satuan",
  "Add Item": "Tambah Item",
  "Add Inventory": "Tambah Inventaris",
  "Add Expense": "Tambah Pengeluaran",
  "Add at least one item with quantity": "Tambahkan minimal satu item dengan jumlah",
  "Purchase recorded — inventory & prices updated": "Pembelian dicatat — stok & harga diperbarui",
  "Branch purchase recorded": "Pembelian cabang dicatat",
  "On confirm: Central inventory increases, Product Master prices update from the invoice, and price history is recorded.": "Saat dikonfirmasi: stok Central bertambah, harga Master Produk diperbarui dari faktur, dan riwayat harga dicatat.",
  "Inventory items increase Branch Inventory. Expense items (Gas, Air Galon, service) are tracked as spending only — no inventory or HPP impact.": "Item inventaris menambah Stok Cabang. Item pengeluaran (Gas, Air Galon, servis) hanya dicatat sebagai pengeluaran — tanpa dampak stok atau HPP.",
  "Invoice Photo": "Foto Faktur",
  "Upload invoice photo": "Unggah foto faktur",
  "Compressing…": "Mengompres…",
  "Could not read image": "Tidak dapat membaca gambar",
  "Invoice": "Faktur",
  "View Invoice": "Lihat Faktur",
  "No invoice": "Tanpa faktur",
  "No invoice image attached": "Tidak ada gambar faktur",
  "No invoice number": "Tanpa nomor faktur",
  "Download": "Unduh",
  "No purchases yet": "Belum ada pembelian",
  "Record your first central purchase.": "Catat pembelian central pertama Anda.",
  "Record your first branch purchase.": "Catat pembelian cabang pertama Anda.",
  "Items": "Item",
  "Item": "Item",
  "Type": "Tipe",
  "Inventory": "Inventaris",
  "Expense": "Pengeluaran",
  "Emergency": "Darurat",
  "Branch": "Cabang",
  "Select branch": "Pilih cabang",
  "Select a branch": "Pilih cabang",
  "Your branch": "Cabang Anda",
  "All branches": "Semua cabang",
  "Supplier / Store": "Supplier / Toko",
  "e.g. Toko Sayur Pak Budi": "mis. Toko Sayur Pak Budi",
  "e.g. Gas LPG, Air Galon": "mis. Gas LPG, Air Galon",
  "Purchases Today": "Pembelian Hari Ini",
  "Purchases This Month": "Pembelian Bulan Ini",
  "Total Value (Month)": "Total Nilai (Bulan)",
  "Top Suppliers (Month)": "Supplier Teratas (Bulan)",
  "Supplier Analytics": "Analitik Supplier",
  "No supplier-linked purchases yet": "Belum ada pembelian terkait supplier",
  "Spend": "Pengeluaran",
  "Count": "Jumlah",
  "Last": "Terakhir",
  "Branch Spending (This Month)": "Pengeluaran Cabang (Bulan Ini)",
  "No branch purchases this month": "Belum ada pembelian cabang bulan ini",
  "Monthly Spending": "Pengeluaran Bulanan",
  "Dashboard": "Dasbor",
  // Stock Adjustment (renamed from Manual Stock In)
  "Stock Adjustment": "Penyesuaian Stok",
  "Save Adjustment": "Simpan Penyesuaian",
  "Stock adjusted": "Stok disesuaikan",
  "Reason is required": "Alasan wajib diisi",
  "For purchases, use the Pembelian Central or Pembelian Cabang menu.": "Untuk pembelian gunakan menu Pembelian Central atau Pembelian Cabang.",
  "Stock Count Adjustment": "Penyesuaian Hitung Stok",
  "Correction": "Koreksi",
  "Found Stock": "Stok Ditemukan",
  "System Fix": "Perbaikan Sistem",
  // --- Phase 6: Recipe Coverage ---
  "Recipe Coverage": "Cakupan Resep",
  "Recipe completeness & sales coverage intelligence": "Intelijen kelengkapan resep & cakupan penjualan",
  "Missing Recipes": "Resep Hilang",
  "Coverage Impact": "Dampak Cakupan",
  "Total Menu": "Total Menu",
  "Recipe Complete": "Resep Lengkap",
  "Missing Recipe": "Belum Ada Resep",
  "Coverage": "Cakupan",
  "Complete": "Lengkap",
  "Top Missing Recipes Requiring Action": "Resep Prioritas yang Perlu Dibuat",
  "sold": "terjual",
  "Most Sold Missing": "Terlaris Tanpa Resep",
  "Top Revenue Missing": "Omzet Tertinggi Tanpa Resep",
  "Menu Name": "Nama Menu",
  "Recipe Status": "Status Resep",
  "Sales Qty": "Jml Penjualan",
  "Sales Value": "Nilai Penjualan",
  "Priority": "Prioritas",
  "Tinggi": "Tinggi",
  "Sedang": "Sedang",
  "Rendah": "Rendah",
  "Total Sales": "Total Penjualan",
  "Covered Sales": "Penjualan Tercakup",
  "Uncovered Sales": "Penjualan Belum Tercakup",
  "units": "unit",
  "Potential Distortion": "Potensi Distorsi",
  "Inventory Distortion": "Distorsi Inventaris",
  "Units sold without depleting inventory": "Unit terjual tanpa mengurangi stok",
  "Margin Distortion": "Distorsi Margin",
  "Sales with no HPP / margin recorded": "Penjualan tanpa HPP / margin tercatat",
  "of sales is not backed by a recipe": "penjualan tidak didukung resep",
  "Monthly Coverage Trend": "Tren Cakupan Bulanan",
  "Track progress toward 100% recipe coverage": "Pantau kemajuan menuju cakupan resep 100%",
  // --- Phase 6: Invoice OCR ---
  "Invoice OCR": "OCR Faktur",
  "Scan Invoice Photo": "Pindai Foto Faktur",
  "Upload a supplier invoice — AI extracts items, matches products, then you review & approve.": "Unggah faktur supplier — AI mengekstrak item, mencocokkan produk, lalu Anda tinjau & setujui.",
  "Upload Invoice Photo": "Unggah Foto Faktur",
  "Scanning invoice…": "Memindai faktur…",
  "Invoice scanned — review the extracted items": "Faktur dipindai — tinjau item yang diekstrak",
  "OCR failed. Try a clearer photo.": "OCR gagal. Coba foto yang lebih jelas.",
  "Invoice Review Queue": "Antrian Tinjauan Faktur",
  "No scanned invoices yet": "Belum ada faktur dipindai",
  "Upload an invoice photo to get started.": "Unggah foto faktur untuk memulai.",
  "Review Invoice": "Tinjau Faktur",
  "Unknown supplier": "Supplier tidak diketahui",
  "Review": "Tinjau",
  "Reject": "Tolak",
  "Approve & Create Purchase": "Setujui & Buat Pembelian",
  "Draft rejected": "Draf ditolak",
  "item(s) still need a product match before approval": "item belum dicocokkan ke produk sebelum disetujui",
  "OCR Text": "Teks OCR",
  "Matched Product": "Produk Cocok",
  "— choose product —": "— pilih produk —",
  "Suggestions": "Saran",
  "All products": "Semua produk",
  "Perlu Review": "Perlu Review",
  "pending": "menunggu",
  // --- Phase 7: Finance ---
  "Finance": "Keuangan",
  "Finance Dashboard": "Dashboard Keuangan",
  "Dashboard Keuangan": "Dashboard Keuangan",
  "Revenue, profit, cash & branch performance at a glance": "Pendapatan, profit, kas & performa cabang sekilas",
  "Profit & Loss": "Laba Rugi",
  "Cash Flow": "Arus Kas",
  "Finance Categories": "Kategori Keuangan",
  "Revenue": "Pendapatan",
  "COGS": "HPP (COGS)",
  "COGS / Food Cost": "HPP / Food Cost",
  "Gross Profit": "Laba Kotor",
  "Operating Expenses": "Biaya Operasional",
  "Net Profit": "Laba Bersih",
  "Cash Position": "Posisi Kas",
  "coverage": "cakupan",
  "cov": "cak",
  "margin": "margin",
  "runway": "runway",
  "months": "bulan",
  "vs last month": "vs bulan lalu",
  "Financial Health": "Kesehatan Finansial",
  "Revenue by Branch": "Pendapatan per Cabang",
  "Profit by Branch": "Laba per Cabang",
  "Top Expenses": "Pengeluaran Terbesar",
  "No expenses recorded for this period": "Belum ada pengeluaran pada periode ini",
  "Gross": "Kotor",
  "Net": "Bersih",
  "profitability": "Profitabilitas",
  "cash_flow": "Arus Kas",
  "efficiency": "Efisiensi",
  "growth": "Pertumbuhan",
  "Set Cash Position": "Atur Posisi Kas",
  "Cash position updated": "Posisi kas diperbarui",
  "Enter your current actual cash & bank balance (not tracked automatically).": "Masukkan saldo kas & bank aktual saat ini (tidak terlacak otomatis).",
  "Current Cash & Bank": "Kas & Bank Saat Ini",
  "Cash": "Kas",
  "Income statement — auto from operations + manual entries": "Laporan laba rugi — otomatis dari operasi + entri manual",
  "Income Statement": "Laporan Laba Rugi",
  "Labor": "Tenaga Kerja",
  "Occupancy": "Sewa & Utilitas",
  "Marketing": "Marketing",
  "Other OPEX": "Biaya Operasional Lain",
  "EBITDA": "EBITDA",
  "Non-Operating (Tax/Interest/Depr.)": "Non-Operasional (Pajak/Bunga/Depresiasi)",
  "KPIs vs Benchmark": "KPI vs Benchmark",
  "target": "target",
  "Prime Cost": "Prime Cost",
  "Sesuai": "Sesuai",
  "Di Atas": "Di Atas",
  "Di Bawah": "Di Bawah",
  "Cash in/out, runway & 12-month projection": "Kas masuk/keluar, runway & proyeksi 12 bulan",
  "Cash In": "Kas Masuk",
  "Cash Out": "Kas Keluar",
  "Net Cash Flow": "Arus Kas Bersih",
  "Supplier": "Supplier",
  "This Month": "Bulan Ini",
  "Sales (auto)": "Penjualan (auto)",
  "Manual cash in": "Kas masuk manual",
  "Supplier payments (auto)": "Pembayaran supplier (auto)",
  "Manual cash out": "Kas keluar manual",
  "12-Month Cash Projection": "Proyeksi Kas 12 Bulan",
  "Worst": "Terburuk",
  "Base": "Dasar",
  "Best": "Terbaik",
  "Manual expenses & category master": "Pengeluaran manual & master kategori",
  "Expenses & Entries": "Pengeluaran & Entri",
  "Categories": "Kategori",
  "Add Category": "Tambah Kategori",
  "Add Finance Category": "Tambah Kategori Keuangan",
  "Category added": "Kategori ditambahkan",
  "Category Name": "Nama Kategori",
  "Source": "Sumber",
  "system": "sistem",
  "Name is required": "Nama wajib diisi",
  "Add Expense / Entry": "Tambah Pengeluaran / Entri",
  "Entry added": "Entri ditambahkan",
  "No entries this period": "Belum ada entri pada periode ini",
  "Add manual expenses like rent, payroll, electricity, marketing, tax.": "Tambahkan pengeluaran manual seperti sewa, gaji, listrik, marketing, pajak.",
  "Category, amount and date are required": "Kategori, jumlah dan tanggal wajib diisi",
  "Select category": "Pilih kategori",
  "Branch (optional)": "Cabang (opsional)",
  "Amount": "Jumlah",

  // --- Phase 7 Sprint 2: Multi-Outlet Analyzer & Scenario Simulator ---
  "Multi-Outlet Analyzer": "Analisis Multi Cabang",
  "Outlet Analyzer": "Analisis Cabang",
  "Compare branch performance — revenue, cost & profit": "Bandingkan performa cabang — pendapatan, biaya & laba",
  "Scenario Simulator": "Simulator Skenario",
  "Model business decisions before you make them": "Modelkan keputusan bisnis sebelum diterapkan",
  "Highest Profit Branch": "Cabang Laba Tertinggi",
  "Lowest Profit Branch": "Cabang Laba Terendah",
  "Highest Growth Branch": "Cabang Pertumbuhan Tertinggi",
  "Lowest Margin Branch": "Cabang Margin Terendah",
  "COGS by Branch": "HPP per Cabang",
  "Gross Profit by Branch": "Laba Kotor per Cabang",
  "Net Profit by Branch": "Laba Bersih per Cabang",
  "Branch Comparison": "Perbandingan Cabang",
  "Overhead": "Overhead",
  "Growth": "Pertumbuhan",
  "Margin": "Margin",
  "Share": "Porsi",
  "Company Total": "Total Perusahaan",
  "no data": "tidak ada data",
  "Net profit allocates company-wide overhead by revenue share; branch-tagged expenses are charged directly.": "Laba bersih mengalokasikan overhead perusahaan berdasarkan porsi pendapatan; biaya bertag cabang dibebankan langsung.",
  "Adjustments": "Penyesuaian",
  "Revenue Change": "Perubahan Pendapatan",
  "Raw Material Cost Change": "Perubahan Biaya Bahan Baku",
  "Payroll Change": "Perubahan Gaji",
  "Rent Change": "Perubahan Sewa",
  "Add a new branch": "Tambah cabang baru",
  "New Branch Simulation": "Simulasi Cabang Baru",
  "Monthly Revenue": "Pendapatan Bulanan",
  "COGS %": "HPP %",
  "Monthly Payroll": "Gaji Bulanan",
  "Monthly Rent": "Sewa Bulanan",
  "Monthly Other OPEX": "Biaya Operasional Lain Bulanan",
  "One-time Setup Cost": "Biaya Setup Satu Kali",
  "Run Simulation": "Jalankan Simulasi",
  "Reset": "Atur Ulang",
  "Revenue Impact": "Dampak Pendapatan",
  "Profit Impact": "Dampak Laba",
  "Cash Flow Impact": "Dampak Arus Kas",
  "Break-even Estimate": "Estimasi Titik Impas",
  "Baseline": "Dasar (Baseline)",
  "Adjusted": "Setelah Skenario",
  "Delta": "Selisih",
  "Break-even Revenue": "Pendapatan Titik Impas",
  "Monthly Fixed Costs": "Biaya Tetap Bulanan",
  "Contribution Margin": "Margin Kontribusi",
  "Margin of Safety": "Margin Keamanan",
  "New Branch Payback": "Balik Modal Cabang Baru",
  "Profitable": "Untung",
  "Not yet profitable": "Belum Untung",
  "per month": "per bulan",
  "one-time": "satu kali",
  "First Month Net": "Bersih Bulan Pertama",
  "Annual Impact": "Dampak Tahunan",
  "Assumptions": "Asumsi",
  "Baseline period": "Periode dasar",
  "improvement": "perbaikan",
  "decline": "penurunan",
  "Comparison": "Perbandingan",

  // --- Phase 7 Sprint 3 (AI CFO) + Phase 8 (Bank Statement Intelligence) ---
  "AI CFO": "AI CFO",
  "Executive financial intelligence — read only": "Intelijen keuangan eksekutif — hanya baca",
  "AI CFO only reads and analyzes. It never edits, approves, changes prices, or creates financial records.": "AI CFO hanya membaca dan menganalisis. Tidak pernah mengubah, menyetujui, mengubah harga, atau membuat catatan keuangan.",
  "Ask your AI CFO": "Tanya AI CFO Anda",
  "Revenue, profitability, cash flow, branch comparison, inventory, purchasing — and what-if scenarios, from your real data.": "Pendapatan, profitabilitas, arus kas, perbandingan cabang, inventory, pembelian — dan skenario what-if, dari data nyata Anda.",
  "AI CFO is analyzing your finances…": "AI CFO sedang menganalisis keuangan Anda…",
  "Ask… e.g. Bagaimana jika harga ayam naik 15%?": "Tanya… mis. Bagaimana jika harga ayam naik 15%?",
  "Bank Statement Intelligence": "Intelijen Rekening Bank",
  "Upload, auto-categorize and approve into finance": "Unggah, kategorisasi otomatis & setujui ke keuangan",
  "Upload Bank Statement": "Unggah Rekening Koran",
  "Account": "Akun",
  "Bank Name": "Nama Bank",
  "Choose file": "Pilih file",
  "Parsing…": "Memproses…",
  "Supported: CSV, XLS, XLSX, PDF. AI detects and categorizes transactions automatically.": "Didukung: CSV, XLS, XLSX, PDF. AI mendeteksi & mengkategorikan transaksi otomatis.",
  "Statements": "Rekening Koran",
  "No statements yet": "Belum ada rekening koran",
  "transactions": "transaksi",
  "Statement parsed": "Rekening koran berhasil diproses",
  "Upload failed": "Gagal mengunggah",
  "Approved": "Disetujui",
  "Draft": "Draf",
  "Approve & Create Entries": "Setujui & Buat Entri",
  "entries created": "entri dibuat",
  "entries": "entri",
  "Approve failed": "Gagal menyetujui",
  "Money In": "Uang Masuk",
  "Money Out": "Uang Keluar",
  "transactions need review before approval": "transaksi perlu ditinjau sebelum disetujui",
  "Category": "Kategori",
  "Skip": "Lewati",
  "Deleted": "Dihapus",

  // --- Phase 8A: Branch-aware finance ---
  "Estimasi": "Estimasi",
  "Semua Cabang": "Semua Cabang",
  "of revenue": "dari pendapatan",
  "Cash Position & Runway are revenue-share estimates (no per-branch bank balance).": "Posisi Kas & Runway adalah estimasi berbasis porsi pendapatan (tidak ada saldo bank per cabang).",
  "Labor Cost Change": "Perubahan Biaya Tenaga Kerja",
  "Occupancy Cost Change": "Perubahan Biaya Okupansi",
  "Marketing Cost Change": "Perubahan Biaya Marketing",
  "Other OPEX Change": "Perubahan Biaya Operasional Lain",
  // --- Phase 3: professional empty states for a freshly provisioned tenant ---
  "No products yet": "Belum ada produk",
  "Add your first product to start building your master data.": "Tambahkan produk pertama Anda untuk mulai menyusun data master.",
  "No branches yet": "Belum ada cabang",
  "Create your first branch to start operating.": "Mulailah dengan membuat cabang pertama Anda untuk mulai beroperasi.",
  "No categories yet": "Belum ada kategori",
  "Add your first category to classify your products.": "Tambahkan kategori pertama untuk mengklasifikasikan produk Anda.",
  "No units yet": "Belum ada unit",
  "Add your first unit of measure (e.g. kg, pcs, liter).": "Tambahkan satuan unit pertama (mis. kg, pcs, liter).",
  "No suppliers yet": "Belum ada pemasok",
  "Add your first supplier to start procurement.": "Tambahkan pemasok pertama untuk memulai pengadaan.",
  "No inventory items yet": "Belum ada item inventaris",
  "Inventory appears here once products are added and stock is received.": "Inventaris akan muncul di sini setelah produk ditambahkan dan stok diterima.",
  "Branch stock levels appear here once branches are set up and stock is received.": "Level stok cabang akan muncul di sini setelah cabang disiapkan dan stok diterima.",
  // --- Phase 4: First-time tenant onboarding ---
  "Welcome to Central Flow": "Selamat Datang di Central Flow",
  "Your restaurant setup is almost ready. Complete these steps to get started.": "Penyiapan restoran Anda hampir siap. Selesaikan langkah-langkah ini untuk memulai.",
  "steps completed": "langkah selesai",
  "Skip onboarding": "Lewati onboarding",
  "Complete Company Profile": "Lengkapi Profil Perusahaan",
  "Create First Branch": "Buat Cabang Pertama",
  "Add Supplier": "Tambah Pemasok",
  "Create Product Categories": "Buat Kategori Produk",
  "Create Products": "Buat Produk",
  "Create Menu": "Buat Menu",
  "Create Recipes": "Buat Resep",
  "Invite Employees": "Undang Karyawan",
  "Congratulations!": "Selamat!",
  "Your Central Flow workspace is now ready for daily operations.": "Workspace Central Flow Anda kini siap untuk operasi harian.",
  "Start operating": "Mulai Beroperasi",
  "Start": "Mulai",
  "New Branch": "Cabang Baru",
  "Branch created": "Cabang dibuat",
  // --- Finance P0: Cost Integrity & Reconciliation ---
  "In Sync": "Tersinkron",
  "Needs Sync": "Perlu Sinkron",
  "Synced": "Tersinkron",
  "Pending sync": "Menunggu Sinkron",
  "Financial Reconciliation": "Rekonsiliasi Keuangan",
  "Verify the financial layer is fully in sync with operations": "Verifikasi lapisan keuangan sepenuhnya selaras dengan operasional",
  "Sync Now": "Sinkron Sekarang",
  "Ledger resynced": "Buku besar disinkronkan ulang",
  "Resync failed": "Sinkronisasi ulang gagal",
  // --- MJ-02 & MN-03 audit fixes ---
  "Post an opening-balance adjustment journal (1300 ↔ 3400) to close the WAC vs GL gap? This action is recorded in the audit trail.": "Posting jurnal penyesuaian saldo awal (1300 ↔ 3400) untuk menutup gap WAC vs GL? Tindakan ini tercatat di jejak audit.",
  "Adjustment journal posted — inventory is now in sync with the GL": "Jurnal penyesuaian diposting — persediaan kini sinkron dengan GL",
  "Post Opening Balance Adjustment (close gap)": "Posting Penyesuaian Saldo Awal (tutup gap)",
  "Cash Position from General Ledger": "Posisi Kas dari Buku Besar (GL)",
  "Total (GL)": "Total (GL)",
  "Manual configuration value": "Nilai konfigurasi manual",
  "Variance (GL − manual)": "Selisih (GL − manual)",
  "Failed": "Gagal",
  "General Ledger Synchronization": "Sinkronisasi Buku Besar",
  "Last synchronized": "Terakhir disinkronkan",
  "Inventory Valuation vs General Ledger": "Valuasi Inventaris vs Buku Besar",
  "Inventory Valuation (WAC)": "Valuasi Inventaris (WAC)",
  "— Central Warehouse": "— Gudang Pusat",
  "— Branches": "— Cabang",
  "Inventory Value in General Ledger": "Nilai Inventaris di Buku Besar",
  "Differences arise where opening stock or inter-branch transfers are not yet journaled. Finance only observes — it never alters operational stock.": "Selisih muncul bila stok awal atau transfer antar-cabang belum dijurnal. Finance hanya mengamati — tidak pernah mengubah stok operasional.",
  "COGS: Theoretical vs Actual (WAC)": "HPP: Teoretis vs Aktual (WAC)",
  "Theoretical COGS (recipe × price)": "HPP Teoretis (resep × harga)",
  "Actual COGS (recipe × WAC)": "HPP Aktual (resep × WAC)",
  "General Ledger COGS": "HPP Buku Besar",
  "Recipe coverage": "Cakupan resep",
  "Items with WAC cost of 0 need ingredient costing (e.g. produced/beverage bases).": "Item dengan biaya WAC 0 perlu penetapan biaya bahan (mis. bahan produksi/minuman).",
  "Top Cost Variance by Menu": "Selisih Biaya Tertinggi per Menu",
  "Theoretical": "Teoretis",
  "Actual (WAC)": "Aktual (WAC)",
  "Module Synchronization Status": "Status Sinkronisasi Modul",
  "Source Records": "Catatan Sumber",
  "Journals": "Jurnal",
  "Module": "Modul",
};

const TRANSLATIONS = { en: {}, id: ID };

const LangCtx = createContext({ lang: "id", setLang: () => {}, t: (s) => s });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("cf_lang") || "id");
  const setLang = useCallback((l) => {
    localStorage.setItem("cf_lang", l);
    setLangState(l);
  }, []);
  const t = useCallback((text, fallback) => {
    if (text == null) return text;
    if (lang === "en") return fallback ?? text;
    const dict = TRANSLATIONS[lang] || {};
    return dict[text] ?? fallback ?? text;
  }, [lang]);
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
}

export const useT = () => useContext(LangCtx);

export function LanguageToggle({ className = "" }) {
  const { lang, setLang } = useT();
  return (
    <div className={`inline-flex overflow-hidden rounded-lg border border-line ${className}`} data-testid="language-toggle">
      {[["id", "ID"], ["en", "EN"]].map(([code, label]) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`lang-${code}`}
          className={`px-2.5 py-1 text-xs font-bold transition-colors ${lang === code ? "bg-primary text-white" : "bg-white text-ink-soft hover:bg-stone-50"}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
